import Phaser from 'phaser';
import { GAME_CONFIG, OrbColor } from '../config/GameConfig';
import { ModuleRenderer } from '../ui/ModuleRenderer';
import { ShipSprite } from '../ui/ShipSprite';
import { getModuleDefinition, getModulesData, getSymbol, ModuleDefinition, PlayerModule, SymbolDraw } from '../data/ModuleData';
import { loadEnemyData, loadEnemyEffectsData, getEnemyEffectType, ASPECT_COLORS, ASPECT_DARK_COLORS, ElementType, getThreatMultipliers, getBudgetsForThreatLevel } from '../data/EnemyData';
import { apiClient } from '../network/ApiClient';
import { ASPECT_TO_ORB, rollAspectsForRarity } from '../utils/aspectUtils';
import { RunPlan, Encounter, GeneratedEnemy, generateRun, getStartEncounter, generateBossEncounter } from '../data/RunGenerator';

interface OrbData {
    color: OrbColor;
    container: Phaser.GameObjects.Container;
    graphics: Phaser.GameObjects.Graphics;
    glowGraphics?: Phaser.GameObjects.Graphics;
    scanlineGraphics?: Phaser.GameObjects.Graphics;
}

interface FittedModule {
    slotIndex: number;
    slotType: 'weapon' | 'support';
    module: PlayerModule | null;
}

interface Star {
    x: number;
    y: number;
    speed: number;
    size: number;
    alpha: number;
}

interface EnemyDisplay {
    enemy: GeneratedEnemy;
    container: Phaser.GameObjects.Container;
    sprite: ShipSprite;
    hpBarBg: Phaser.GameObjects.Graphics;
    hpBarFill: Phaser.GameObjects.Graphics;
    hpText: Phaser.GameObjects.Text;
    countdownText: Phaser.GameObjects.Text;
    targetHighlight?: Phaser.GameObjects.Graphics;
    // Store HP bar dimensions for updateEnemyHP
    hpBarWidth: number;
    hpBarHeight: number;
    scale: number;
}

interface CombatModuleDisplay {
    fittedModule: FittedModule;
    container: Phaser.GameObjects.Container;
    chargeBarBg: Phaser.GameObjects.Graphics;
    chargeBarFill: Phaser.GameObjects.Graphics;
    multiplierText: Phaser.GameObjects.Text;
    // Combat state
    chargeCost: number;
    chargeSources: string[];
    chargePerOrb: number;
    currentCharge: number;
    displayedCharge: number;  // For animation - tracks what's visually shown
    displayedFillCount: number;  // For animation
    fillCount: number;  // How many times bar has filled this turn
    primaryColor: number;  // Color for the charge bar
    baseDamage: number;
    damageType: string;
    weaponKey: string;  // Weapon type identifier for multiplier lookup (laser, missile, etc.)
    originalY: number;  // Original Y position for resetting after animations
}

export class GameScene extends Phaser.Scene {
    // Layout constants - Board on LEFT (70%), Space on RIGHT (30%)
    private readonly BOARD_WIDTH: number;
    private readonly SPACE_WIDTH: number;
    private readonly SPACE_X: number;

    // Right panel vertical layout
    private readonly TOP_UI_HEIGHT: number;      // 10%
    private readonly ENEMY_AREA_HEIGHT: number;  // 40%
    private readonly PLAYER_AREA_HEIGHT: number; // 40%
    private readonly BOTTOM_UI_HEIGHT: number;   // 10%

    // Board state
    private board: (OrbColor | null)[][] = [];
    private orbs: (OrbData | null)[][] = [];
    private boardContainer!: Phaser.GameObjects.Container;

    // Space area state
    private spaceContainer!: Phaser.GameObjects.Container;
    private threatLevel = 5;
    private fittedModules: FittedModule[] = [];
    private moduleRenderer!: ModuleRenderer;
    private shipSprite!: ShipSprite;

    // Star field
    private stars: Star[] = [];
    private starGraphics!: Phaser.GameObjects.Graphics;
    private starfieldTop = 0;
    private starfieldBottom = 0;

    // Combat state
    private playerShield: number = GAME_CONFIG.BASE_SHIELD;
    private playerMaxShield: number = GAME_CONFIG.BASE_SHIELD;

    // Player stats from passive effects
    private playerResists: Record<string, number> = {
        THERMAL: 0, CRYO: 0, RADIATION: 0, EMP: 0, VOID: 0,
    };
    private shieldGainMultiplier = 1.0;
    private moveTimeBonus = 0; // Seconds added to base move time
    private moveTimeBonusTurns = 0; // Turns remaining for move time buff
    private chargeBonuses: Array<{ color: string; bonusPerOrb: number; appliesTo: string }> = [];
    // Weapon damage/charge multipliers from support modules (per damage type)
    // Keyed by weaponType (laser, railgun, missile, cryo, pulse)
    private weaponDamageMultipliers: Record<string, number> = {
        laser: 1.0, railgun: 1.0, missile: 1.0, cryo: 1.0, pulse: 1.0,
    };
    private weaponChargeCostMultipliers: Record<string, number> = {
        laser: 1.0, railgun: 1.0, missile: 1.0, cryo: 1.0, pulse: 1.0,
    };

    // Enemy effect state - debuffs applied by enemies
    private moveTimeDebuffTurns = 0; // Turns remaining for move time debuff
    private moveTimeDebuffAmount = 0; // Seconds reduced from move time
    private healBlockMultiplier = 1.0; // Multiplier for shield gains (< 1 means reduced healing)
    private healBlockTurns = 0; // Turns remaining for heal block debuff
    private spawnBiases: Array<{ color: OrbColor; weightAdd: number; refillsRemaining: number }> = [];
    private lockedCells: Array<{ col: number; row: number; turnsRemaining: number }> = [];
    private lockedCellGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();

    // Enemy buff state
    private enemyDamageMultiplier = 1.0;
    private enemyDamageMultiplierTurns = 0;
    private enemyResistBuffs: Array<{ enemyId: string; damageType: string; resistAdd: number; turnsRemaining: number }> = [];

    // Run/Enemy state
    private runPlan: RunPlan | null = null;
    private currentEncounter: Encounter | null = null;
    private enemyDisplays: EnemyDisplay[] = [];
    private enemyContainer!: Phaser.GameObjects.Container;
    private targetedEnemyIndex: number = 0; // Index of currently targeted enemy

    // Mutator state
    private activeMutators: string[] = [];
    private mutatorEffects: Map<string, number> = new Map();

    // Run progression state
    private currentPosition = 1;
    private currentPath: 'left' | 'right' | 'center' = 'center';
    private isTreasureEncounter = false;
    private treasureCounter = 0;
    private treasureTurnsRemaining = 5;
    private treasureCounterText: Phaser.GameObjects.Text | null = null;
    private treasureTurnsText: Phaser.GameObjects.Text | null = null;
    private isWarping = false;
    private isChoosingPath = false;

    // Drag state
    private isDragging = false;
    private draggedOrb: OrbData | null = null;
    private dragStartPos: { col: number; row: number } | null = null;
    private currentDragPos: { col: number; row: number } | null = null;
    private swapOccurred = false; // Track if any swap happened during drag

    // Animation state
    private isAnimating = false;
    private cascadeCount = 0;

    // Timer UI
    private timerBarBg!: Phaser.GameObjects.Graphics;
    private timerBarFill!: Phaser.GameObjects.Graphics;
    private timerTween: Phaser.Tweens.Tween | null = null;
    private timerProgress = 1;

    // Combat module displays
    private combatModules: CombatModuleDisplay[] = [];
    private moduleSize = 0;
    private moduleStartX = 0;
    private moduleY = 0;

    // Match tracking for turn resolution
    private turnMatchCounts: Record<OrbColor, number> = { R: 0, B: 0, G: 0, Y: 0, P: 0, S: 0 };
    private pendingParticles: Array<{ fromX: number; fromY: number; toModule: CombatModuleDisplay; color: number }> = [];

    // Shield bar position (for animations)
    private shieldBarX = 0;
    private shieldBarY = 0;
    private shieldBarWidth = 0;
    private shieldBarHeight = 0;

    // Game state
    private isGameOver = false;
    private isGuest = false;

    // Scrap tracking
    private scrapEarned = 0;
    private scrapText!: Phaser.GameObjects.Text;
    private scrapIcon!: Phaser.GameObjects.Graphics;

    constructor() {
        super({ key: 'GameScene' });

        // Left 70% for board, right 30% for space
        this.BOARD_WIDTH = GAME_CONFIG.WIDTH * 0.70;
        this.SPACE_WIDTH = GAME_CONFIG.WIDTH * 0.30;
        this.SPACE_X = this.BOARD_WIDTH;

        // Right panel vertical sections:
        // - Top 10% reserved UI
        // - Middle 80% battle scene (40% enemy, 60% player)
        // - Bottom 10% reserved UI (retreat button)
        this.TOP_UI_HEIGHT = GAME_CONFIG.HEIGHT * 0.10;
        this.ENEMY_AREA_HEIGHT = GAME_CONFIG.HEIGHT * 0.32;  // 40% of middle 80%
        this.PLAYER_AREA_HEIGHT = GAME_CONFIG.HEIGHT * 0.48; // 60% of middle 80%
        this.BOTTOM_UI_HEIGHT = GAME_CONFIG.HEIGHT * 0.10;
    }

    init(data: { threatLevel?: number; fittedModules?: FittedModule[]; runPlan?: RunPlan; isGuest?: boolean; activeMutators?: string[] }): void {
        this.threatLevel = data.threatLevel || 5;
        this.fittedModules = data.fittedModules || [];
        this.runPlan = data.runPlan || null;
        this.isGuest = data.isGuest || false;
        this.activeMutators = data.activeMutators || [];
        this.mutatorEffects.clear();

        // Reset all game state for new run
        this.isGameOver = false;
        this.isAnimating = false;
        this.isDragging = false;
        this.draggedOrb = null;
        this.dragStartPos = null;
        this.currentDragPos = null;
        this.cascadeCount = 0;
        this.playerShield = GAME_CONFIG.BASE_SHIELD;
        this.playerMaxShield = GAME_CONFIG.BASE_SHIELD;
        this.turnMatchCounts = { R: 0, B: 0, G: 0, Y: 0, P: 0, S: 0 };
        this.combatModules = [];
        this.enemyDisplays = [];
        this.stars = [];

        // Run progression state
        this.currentPosition = 1;
        this.currentPath = 'center';
        this.isTreasureEncounter = false;
        this.treasureCounter = 0;
        this.treasureTurnsRemaining = 5;
        this.isWarping = false;
        this.isChoosingPath = false;
        this.warpSpeedMultiplier = 1;
        this.warpOverlay = null;

        // Scrap tracking
        this.scrapEarned = 0;

        // Player stats from passive effects
        this.playerResists = { THERMAL: 0, CRYO: 0, RADIATION: 0, EMP: 0, VOID: 0 };
        this.shieldGainMultiplier = 1.0;
        this.moveTimeBonus = 0;
        this.moveTimeBonusTurns = 0;
        this.chargeBonuses = [];
        this.weaponDamageMultipliers = { laser: 1.0, railgun: 1.0, missile: 1.0, cryo: 1.0, pulse: 1.0 };
        this.weaponChargeCostMultipliers = { laser: 1.0, railgun: 1.0, missile: 1.0, cryo: 1.0, pulse: 1.0 };

        // Enemy effect state
        this.moveTimeDebuffTurns = 0;
        this.moveTimeDebuffAmount = 0;
        this.healBlockMultiplier = 1.0;
        this.healBlockTurns = 0;
        this.spawnBiases = [];
        this.lockedCells = [];
        this.lockedCellGraphics.clear();
        this.enemyDamageMultiplier = 1.0;
        this.enemyDamageMultiplierTurns = 0;
        this.enemyResistBuffs = [];
    }

    async create(): Promise<void> {
        try {
            this.moduleRenderer = new ModuleRenderer(this);

            // Load enemy data and generate run if needed
            await Promise.all([loadEnemyData(), loadEnemyEffectsData()]);
            if (!this.runPlan) {
                this.runPlan = generateRun(this.threatLevel);
            }
            this.currentEncounter = getStartEncounter(this.runPlan);

            // Apply passive effects from fitted modules at run start
            this.applyPassiveEffects();

            // Apply mutator effects
            this.applyMutators();

            this.initializeBoard();
            this.createBoardDisplay();
            this.createSpaceArea();
            this.createTimerBar();
            this.setupInput();
            this.startScanlineAnimation();

            console.log('GameScene created successfully, orbs:', this.orbs.length, 'x', this.orbs[0]?.length);
        } catch (error) {
            console.error('Error in GameScene.create():', error);
        }
    }

    private applyPassiveEffects(): void {
        // Set base shield from ship data in JSON
        const modulesData = getModulesData();
        if (modulesData?.ship?.baseStats?.shieldMax) {
            this.playerMaxShield = modulesData.ship.baseStats.shieldMax;
            this.playerShield = this.playerMaxShield;
        }

        // Process all fitted modules for passive effects
        for (const fm of this.fittedModules) {
            if (!fm.module) continue;

            const def = getModuleDefinition(fm.module.definitionId);
            if (!def || !def.passiveEffects) continue;

            const level = fm.module.level;
            const scaling = def.scaling as Record<string, number> | undefined;

            for (const effect of def.passiveEffects) {
                this.applyEffect(effect.effectId, effect.params, level, scaling);
            }
        }
    }

    private applyMutators(): void {
        // Process active mutators
        for (const mutatorId of this.activeMutators) {
            // Cache mutator effects by ID for quick lookup
            switch (mutatorId) {
                case 'mut_overclocked': // +25% damage, -10% max shield
                    this.mutatorEffects.set('damage_mult', 0.25);
                    this.playerMaxShield *= 0.90;
                    this.playerShield = this.playerMaxShield; // Reset to new max
                    break;

                case 'mut_efficient_caps': // -2 charge cost on all weapons
                    this.mutatorEffects.set('charge_cost_reduction', 2);
                    break;

                case 'mut_extended_warranty': // +50% shield from S orbs
                    this.shieldGainMultiplier += 0.5;
                    break;

                case 'mut_cascade_master': // +10% damage per cascade
                    this.mutatorEffects.set('cascade_damage_bonus', 0.10);
                    break;

                case 'mut_radiation_leak': // S orbs don't spawn
                    this.mutatorEffects.set('disable_shield_orbs', 1);
                    break;

                case 'mut_solar_flare': // -1s move time
                    this.mutatorEffects.set('move_time_penalty', -1.0);
                    break;

                case 'mut_aggressive_hostiles': // Enemy cooldowns -1
                    this.mutatorEffects.set('enemy_cooldown_reduction', 1);
                    break;

                case 'mut_glass_cannon': // +100% damage dealt/taken
                    this.mutatorEffects.set('glass_cannon_mult', 1.0);
                    break;

                default:
                    console.warn(`Unknown mutator: ${mutatorId}`);
            }
        }

        console.log('Applied mutators:', Array.from(this.mutatorEffects.entries()));
    }

    private applyEffect(
        effectId: string,
        params: Record<string, unknown>,
        level: number = 1,
        scaling?: Record<string, number>
    ): void {
        const levelBonus = level - 1; // Level 1 = no bonus, Level 10 = 9x bonus

        switch (effectId) {
            case 'eff_add_shield_max': {
                const baseAdd = (params.add as number) || 0;
                const perLevel = scaling?.addPerLevel || 0;
                const totalAdd = baseAdd + perLevel * levelBonus;
                this.playerMaxShield += totalAdd;
                this.playerShield = this.playerMaxShield; // Start at full
                break;
            }

            case 'eff_add_resist': {
                const damageType = params.damageType as string;
                const baseResist = (params.resistAdd as number) || 0;
                const perLevel = scaling?.resistPerLevel || 0;
                const totalResist = baseResist + perLevel * levelBonus;
                if (damageType && this.playerResists[damageType] !== undefined) {
                    // Cap resist at 60% (0.6)
                    this.playerResists[damageType] = Math.min(0.6, this.playerResists[damageType] + totalResist);
                }
                break;
            }

            case 'eff_shield_gain_multiplier': {
                const baseMult = (params.multiplierAdd as number) || 0;
                const perLevel = scaling?.multiplierPerLevel || 0;
                const totalMult = baseMult + perLevel * levelBonus;
                this.shieldGainMultiplier += totalMult;
                break;
            }

            case 'eff_charge_bonus_on_match': {
                const color = params.color as string;
                const baseBonus = (params.bonusPerOrb as number) || 0;
                const perLevel = scaling?.bonusPerLevel || 0;
                const totalBonus = baseBonus + perLevel * levelBonus;
                const appliesTo = (params.appliesTo as string) || 'all';
                this.chargeBonuses.push({ color, bonusPerOrb: totalBonus, appliesTo });
                break;
            }

            case 'eff_add_move_time': {
                const seconds = (params.seconds as number) || 0;
                const durationTurns = (params.durationTurns as number) || 1;
                this.moveTimeBonus += seconds;
                this.moveTimeBonusTurns = Math.max(this.moveTimeBonusTurns, durationTurns);
                break;
            }

            case 'eff_weapon_damage_multiplier': {
                const weaponType = params.weaponType as string;
                const baseMult = (params.multiplierAdd as number) || 0;
                const perLevel = scaling?.damageMultPerLevel || 0;
                const totalMult = baseMult + perLevel * levelBonus;
                if (weaponType && this.weaponDamageMultipliers[weaponType] !== undefined) {
                    this.weaponDamageMultipliers[weaponType] += totalMult;
                }
                break;
            }

            case 'eff_weapon_charge_cost_multiplier': {
                const weaponType = params.weaponType as string;
                const baseMult = (params.multiplierAdd as number) || 0;
                const perLevel = scaling?.costMultPerLevel || 0;
                const totalMult = baseMult + perLevel * levelBonus;
                if (weaponType && this.weaponChargeCostMultipliers[weaponType] !== undefined) {
                    this.weaponChargeCostMultipliers[weaponType] += totalMult;
                }
                break;
            }

            case 'eff_add_fit_budget':
                // Handled in BaseScene for fitting, not needed in combat
                break;

            // Skill effects handled separately during skill casting
            case 'eff_weapon_fire':
            case 'eff_skill_convert_random':
            case 'eff_skill_lock_cells':
            case 'eff_skill_loot_boost':
                // These are handled elsewhere
                break;

            default:
                console.warn(`Unknown effect: ${effectId}`);
        }
    }

    // Elemental matchup: THERMAL > CRYO > EMP > RADIATION > VOID > THERMAL
    private getElementalMultiplier(attackType: string, defenderAspects: string[]): number {
        const strongAgainst: Record<string, string> = {
            THERMAL: 'CRYO',
            CRYO: 'EMP',
            EMP: 'RADIATION',
            RADIATION: 'VOID',
            VOID: 'THERMAL',
        };

        const weakAgainst: Record<string, string> = {
            THERMAL: 'VOID',
            CRYO: 'THERMAL',
            EMP: 'CRYO',
            RADIATION: 'EMP',
            VOID: 'RADIATION',
        };

        let multiplier = 1.0;

        for (const aspect of defenderAspects) {
            if (strongAgainst[attackType] === aspect) {
                multiplier = Math.max(multiplier, 1.25); // Strong vs
            } else if (weakAgainst[attackType] === aspect) {
                multiplier = Math.min(multiplier, 0.75); // Weak vs
            }
        }

        return multiplier;
    }

    // Decrement turn-based buffs and debuffs at end of turn
    private tickBuffs(): void {
        // Player buffs
        if (this.moveTimeBonusTurns > 0) {
            this.moveTimeBonusTurns--;
            if (this.moveTimeBonusTurns <= 0) {
                this.moveTimeBonus = 0;
            }
        }

        // Player debuffs (from enemy effects)
        if (this.moveTimeDebuffTurns > 0) {
            this.moveTimeDebuffTurns--;
            if (this.moveTimeDebuffTurns <= 0) {
                this.moveTimeDebuffAmount = 0;
            }
        }
        if (this.healBlockTurns > 0) {
            this.healBlockTurns--;
            if (this.healBlockTurns <= 0) {
                this.healBlockMultiplier = 1.0;
            }
        }

        // Note: Locked cells are decremented at the START of resolveTurn(), not here
        // This ensures they block the player's drag phase for the correct number of turns

        // Enemy buffs
        if (this.enemyDamageMultiplierTurns > 0) {
            this.enemyDamageMultiplierTurns--;
            if (this.enemyDamageMultiplierTurns <= 0) {
                this.enemyDamageMultiplier = 1.0;
            }
        }

        // Enemy resist buffs
        this.enemyResistBuffs = this.enemyResistBuffs.filter(buff => {
            buff.turnsRemaining--;
            return buff.turnsRemaining > 0;
        });
    }

    update(time: number, delta: number): void {
        // Guard against update running before create finishes
        if (this.starGraphics) {
            this.updateStarField(delta);
        }
    }

    // ==================== SPACE AREA (RIGHT 30%) ====================

    private createSpaceArea(): void {
        this.spaceContainer = this.add.container(this.SPACE_X, 0);

        // Dark background for entire right panel
        const bg = this.add.graphics();
        bg.fillStyle(0x0a0a12, 1);
        bg.fillRect(0, 0, this.SPACE_WIDTH, GAME_CONFIG.HEIGHT);
        this.spaceContainer.add(bg);

        // Divider line
        const divider = this.add.graphics();
        divider.lineStyle(2, 0x333355, 0.5);
        divider.lineBetween(0, 0, 0, GAME_CONFIG.HEIGHT);
        this.spaceContainer.add(divider);

        // Calculate starfield bounds (enemy + player areas only)
        this.starfieldTop = this.TOP_UI_HEIGHT;
        this.starfieldBottom = this.TOP_UI_HEIGHT + this.ENEMY_AREA_HEIGHT + this.PLAYER_AREA_HEIGHT;

        // Create starfield for the middle 80% (enemy + player areas)
        this.createStarField();

        // Create areas
        this.createTopUI();
        this.createEnemyArea();
        this.createPlayerArea();
        this.createBottomUI();
    }

    private createStarField(): void {
        this.starGraphics = this.add.graphics();
        this.spaceContainer.add(this.starGraphics);

        // Create stars only in the starfield region
        const starCount = 80;
        const starfieldHeight = this.starfieldBottom - this.starfieldTop;

        for (let i = 0; i < starCount; i++) {
            this.stars.push({
                x: Math.random() * this.SPACE_WIDTH,
                y: this.starfieldTop + Math.random() * starfieldHeight,
                speed: 0.02 + Math.random() * 0.08,
                size: 0.5 + Math.random() * 1.5,
                alpha: 0.3 + Math.random() * 0.7,
            });
        }
    }

    private updateStarField(delta: number): void {
        this.starGraphics.clear();

        const starfieldHeight = this.starfieldBottom - this.starfieldTop;

        for (const star of this.stars) {
            // Move stars downward
            star.y += star.speed * delta;

            // Wrap around within starfield bounds
            if (star.y > this.starfieldBottom) {
                star.y = this.starfieldTop - 10;
                star.x = Math.random() * this.SPACE_WIDTH;
            }

            // Only draw if within starfield bounds
            if (star.y >= this.starfieldTop && star.y <= this.starfieldBottom) {
                const twinkle = 0.9 + Math.sin(star.y * 0.1) * 0.1;
                this.starGraphics.fillStyle(0xffffff, star.alpha * twinkle);
                this.starGraphics.fillCircle(star.x, star.y, star.size);
            }
        }
    }

    private createTopUI(): void {
        // Scrap earned display in upper right of enemy area
        const scrapX = this.SPACE_WIDTH - 30;
        const scrapY = this.TOP_UI_HEIGHT / 2;

        // Scrap icon (stylized gear/currency)
        this.scrapIcon = this.add.graphics();
        this.scrapIcon.fillStyle(0xf1c40f, 1);
        this.scrapIcon.fillCircle(scrapX - 40, scrapY, 10);
        this.scrapIcon.fillStyle(0x0a0a12, 1);
        this.scrapIcon.fillCircle(scrapX - 40, scrapY, 4);
        this.spaceContainer.add(this.scrapIcon);

        // Scrap amount text
        this.scrapText = this.add.text(scrapX - 25, scrapY, `${this.scrapEarned}`, {
            fontFamily: 'monospace',
            fontSize: '28px',
            color: '#f1c40f',
            fontStyle: 'bold',
        });
        this.scrapText.setOrigin(0, 0.5);
        this.spaceContainer.add(this.scrapText);
    }

    private updateScrapDisplay(): void {
        if (this.scrapText) {
            this.scrapText.setText(`${this.scrapEarned}`);
        }
    }

    private createEnemyArea(): void {
        // Create container for enemies
        this.enemyContainer = this.add.container(0, 0);
        this.spaceContainer.add(this.enemyContainer);

        // Render current encounter enemies
        if (this.currentEncounter && this.currentEncounter.enemies.length > 0) {
            this.renderEnemies(this.currentEncounter.enemies);
        }
    }

    private renderEnemies(enemies: GeneratedEnemy[]): void {
        // Clear existing enemy displays
        this.clearEnemyDisplays();

        const enemyAreaTop = this.TOP_UI_HEIGHT;
        const enemyAreaBottom = this.TOP_UI_HEIGHT + this.ENEMY_AREA_HEIGHT;
        const enemyAreaHeight = enemyAreaBottom - enemyAreaTop;
        const centerX = this.SPACE_WIDTH / 2;

        // Calculate layout based on enemy count
        const enemyCount = enemies.length;
        const maxPerRow = 3;
        const rows = Math.ceil(enemyCount / maxPerRow);
        const enemiesPerRow = Math.ceil(enemyCount / rows);

        // Enemy size scales with count
        const baseScale = enemyCount <= 2 ? 0.5 : enemyCount <= 4 ? 0.4 : 0.3;
        const shipHeight = 267 * baseScale;
        const shipWidth = 250 * baseScale;

        // HP bar dimensions
        const hpBarWidth = shipWidth * 0.8;
        const hpBarHeight = 8;

        // Calculate vertical spacing
        const totalRowHeight = shipHeight + hpBarHeight + 40; // ship + hp bar + padding
        const startY = enemyAreaTop + (enemyAreaHeight - rows * totalRowHeight) / 2 + shipHeight / 2;

        // Get run aspect for tinting
        const aspect = this.runPlan?.aspects[0] || 'VOID';
        const tintColor = ASPECT_DARK_COLORS[aspect];

        let enemyIndex = 0;
        for (let row = 0; row < rows && enemyIndex < enemyCount; row++) {
            const enemiesInThisRow = Math.min(enemiesPerRow, enemyCount - enemyIndex);
            const rowWidth = enemiesInThisRow * shipWidth + (enemiesInThisRow - 1) * 20;
            const rowStartX = centerX - rowWidth / 2 + shipWidth / 2;
            const rowY = startY + row * totalRowHeight;

            for (let col = 0; col < enemiesInThisRow && enemyIndex < enemyCount; col++) {
                const enemy = enemies[enemyIndex];
                const x = rowStartX + col * (shipWidth + 20);
                const y = rowY;

                const display = this.createEnemyDisplay(enemy, x, y, baseScale, hpBarWidth, hpBarHeight, tintColor);
                this.enemyDisplays.push(display);
                enemyIndex++;
            }
        }

        // Set up targeting after all enemies are created
        this.setupEnemyTargeting();
    }

    private createEnemyDisplay(
        enemy: GeneratedEnemy,
        x: number,
        y: number,
        scale: number,
        hpBarWidth: number,
        hpBarHeight: number,
        _tintColor: number
    ): EnemyDisplay {
        // Container for this enemy
        const container = this.add.container(x, y);
        this.enemyContainer.add(container);

        // Target highlight (gold shadow behind ship) - added first so it's behind
        const targetHighlight = this.add.graphics();
        targetHighlight.setVisible(false);
        container.add(targetHighlight);

        // Get ship model and tint based on enemy's primary aspect
        const modelType = ShipSprite.getModelForAspect(enemy.aspects);
        const enemyAspect = enemy.aspects[0] || 'VOID';
        const enemyTint = ASPECT_DARK_COLORS[enemyAspect as ElementType];

        // Create ship sprite (rotated 180 degrees to face player)
        const sprite = new ShipSprite(this, 0, 0, {
            scale,
            modelType,
            colors: {
                hull: enemyTint,
                front: enemyTint,
                rear: enemyTint,
                wings: enemyTint,
                engines: enemyTint,
            },
        });
        sprite.container.setRotation(Math.PI); // Rotate to face down (toward player)
        container.add(sprite.container);

        // HP bar background (below ship)
        const hpBarY = 267 * scale / 2 + 15;
        const hpBarBg = this.add.graphics();
        hpBarBg.fillStyle(0x333333, 1);
        hpBarBg.fillRoundedRect(-hpBarWidth / 2, hpBarY, hpBarWidth, hpBarHeight, 3);
        container.add(hpBarBg);

        // HP bar fill
        const hpBarFill = this.add.graphics();
        this.drawHPBar(hpBarFill, enemy.currentHP, enemy.maxHP, hpBarWidth, hpBarHeight, hpBarY);
        container.add(hpBarFill);

        // HP text (24px min for accessibility)
        const hpText = this.add.text(0, hpBarY + hpBarHeight + 8, `${enemy.currentHP}/${enemy.maxHP}`, {
            fontFamily: 'monospace',
            fontSize: '24px',
            color: '#ffffff',
        }).setOrigin(0.5, 0);
        container.add(hpText);

        // Turn countdown (upper left of enemy)
        const countdownY = -267 * scale / 2 - 10;
        const countdownX = -hpBarWidth / 2;
        const minCooldown = Math.min(...enemy.actions.map(a => a.currentCooldown));
        const countdownText = this.add.text(countdownX, countdownY, minCooldown.toString(), {
            fontFamily: 'monospace',
            fontSize: '24px',
            fontStyle: 'bold',
            color: '#ff6666',
            stroke: '#000000',
            strokeThickness: 3,
        }).setOrigin(0, 1);
        container.add(countdownText);

        // Make container interactive for targeting
        const hitAreaSize = 267 * scale;
        container.setSize(hitAreaSize, hitAreaSize);
        container.setInteractive({ useHandCursor: true });

        // Draw the target highlight shape (will be shown/hidden based on targeting)
        this.drawTargetHighlight(targetHighlight, scale);

        return {
            enemy,
            container,
            sprite,
            hpBarBg,
            hpBarFill,
            hpText,
            countdownText,
            targetHighlight,
            hpBarWidth,
            hpBarHeight,
            scale,
        };
    }

    private drawTargetHighlight(graphics: Phaser.GameObjects.Graphics, scale: number): void {
        graphics.clear();
        const size = 140 * scale;

        // Gold glow layers (multiple circles for soft glow effect)
        graphics.fillStyle(0xffd700, 0.15);
        graphics.fillCircle(0, 0, size * 1.2);
        graphics.fillStyle(0xffd700, 0.2);
        graphics.fillCircle(0, 0, size);
        graphics.fillStyle(0xffd700, 0.25);
        graphics.fillCircle(0, 0, size * 0.8);
    }

    private setupEnemyTargeting(): void {
        // Reset target to first living enemy
        this.targetedEnemyIndex = 0;

        // Add click handlers to each enemy
        for (let i = 0; i < this.enemyDisplays.length; i++) {
            const display = this.enemyDisplays[i];
            const index = i;

            display.container.on('pointerdown', () => {
                // Only allow targeting living enemies
                if (display.enemy.currentHP > 0) {
                    this.setTargetedEnemy(index);
                }
            });
        }

        // Set initial target with reticle animation
        if (this.enemyDisplays.length > 0) {
            this.updateTargetHighlights();
            this.showTargetReticle(this.enemyDisplays[0]);
        }
    }

    private setTargetedEnemy(index: number): void {
        // Don't do anything if already targeted
        if (index === this.targetedEnemyIndex) return;

        // Validate index
        if (index < 0 || index >= this.enemyDisplays.length) return;

        // Don't target dead enemies
        if (this.enemyDisplays[index].enemy.currentHP <= 0) return;

        this.targetedEnemyIndex = index;
        this.updateTargetHighlights();
        this.showTargetReticle(this.enemyDisplays[index]);
    }

    private updateTargetHighlights(): void {
        for (let i = 0; i < this.enemyDisplays.length; i++) {
            const display = this.enemyDisplays[i];
            if (display.targetHighlight) {
                const isTargeted = i === this.targetedEnemyIndex && display.enemy.currentHP > 0;
                display.targetHighlight.setVisible(isTargeted);
            }
        }
    }

    private showTargetReticle(display: EnemyDisplay): void {
        const x = this.SPACE_X + display.container.x;
        const y = display.container.y;
        const scale = display.sprite.container.scaleX;
        const size = 120 * scale;

        // Create reticle graphics
        const reticle = this.add.graphics();
        reticle.setPosition(x, y);
        reticle.setDepth(800);

        // Draw reticle pattern - crosshair style
        const drawReticle = () => {
            reticle.clear();

            // Outer ring
            reticle.lineStyle(3, 0xffd700, 0.9);
            reticle.strokeCircle(0, 0, size);

            // Inner ring
            reticle.lineStyle(2, 0xffd700, 0.7);
            reticle.strokeCircle(0, 0, size * 0.6);

            // Crosshair lines (extending beyond outer ring)
            reticle.lineStyle(3, 0xffd700, 0.9);
            // Top
            reticle.lineBetween(0, -size * 0.7, 0, -size * 1.3);
            // Bottom
            reticle.lineBetween(0, size * 0.7, 0, size * 1.3);
            // Left
            reticle.lineBetween(-size * 0.7, 0, -size * 1.3, 0);
            // Right
            reticle.lineBetween(size * 0.7, 0, size * 1.3, 0);

            // Corner brackets
            const bracketSize = size * 0.3;
            const bracketOffset = size * 0.85;
            reticle.lineStyle(2, 0xffd700, 0.8);

            // Top-left bracket
            reticle.lineBetween(-bracketOffset, -bracketOffset + bracketSize, -bracketOffset, -bracketOffset);
            reticle.lineBetween(-bracketOffset, -bracketOffset, -bracketOffset + bracketSize, -bracketOffset);

            // Top-right bracket
            reticle.lineBetween(bracketOffset, -bracketOffset + bracketSize, bracketOffset, -bracketOffset);
            reticle.lineBetween(bracketOffset, -bracketOffset, bracketOffset - bracketSize, -bracketOffset);

            // Bottom-left bracket
            reticle.lineBetween(-bracketOffset, bracketOffset - bracketSize, -bracketOffset, bracketOffset);
            reticle.lineBetween(-bracketOffset, bracketOffset, -bracketOffset + bracketSize, bracketOffset);

            // Bottom-right bracket
            reticle.lineBetween(bracketOffset, bracketOffset - bracketSize, bracketOffset, bracketOffset);
            reticle.lineBetween(bracketOffset, bracketOffset, bracketOffset - bracketSize, bracketOffset);
        };

        drawReticle();

        // Animate: spin half rotation while fading out
        reticle.setScale(1.5);
        reticle.setAlpha(0);

        // Pop in
        this.tweens.add({
            targets: reticle,
            scaleX: 1,
            scaleY: 1,
            alpha: 1,
            duration: 150,
            ease: 'Back.easeOut',
            onComplete: () => {
                // Spin and fade out
                this.tweens.add({
                    targets: reticle,
                    rotation: Math.PI, // Half rotation
                    alpha: 0,
                    scaleX: 0.8,
                    scaleY: 0.8,
                    duration: 1200,
                    ease: 'Cubic.easeIn',
                    onComplete: () => reticle.destroy(),
                });
            },
        });
    }

    private getTargetedEnemy(): EnemyDisplay | null {
        // Get the currently targeted enemy if alive
        if (this.targetedEnemyIndex >= 0 && this.targetedEnemyIndex < this.enemyDisplays.length) {
            const target = this.enemyDisplays[this.targetedEnemyIndex];
            if (target.enemy.currentHP > 0) {
                return target;
            }
        }

        // Find next living enemy
        for (let i = 0; i < this.enemyDisplays.length; i++) {
            if (this.enemyDisplays[i].enemy.currentHP > 0) {
                this.targetedEnemyIndex = i;
                this.updateTargetHighlights();
                return this.enemyDisplays[i];
            }
        }

        return null;
    }

    private drawHPBar(
        graphics: Phaser.GameObjects.Graphics,
        currentHP: number,
        maxHP: number,
        width: number,
        height: number,
        y: number
    ): void {
        graphics.clear();
        const hpPercent = Math.max(0, currentHP / maxHP);
        const fillWidth = width * hpPercent;

        // Color based on HP percentage
        let color = 0x44ff44; // Green
        if (hpPercent < 0.3) color = 0xff4444; // Red
        else if (hpPercent < 0.6) color = 0xffaa44; // Orange

        graphics.fillStyle(color, 1);
        graphics.fillRoundedRect(-width / 2, y, fillWidth, height, 3);
    }

    private clearEnemyDisplays(): void {
        for (const display of this.enemyDisplays) {
            display.sprite.destroy();
            display.container.destroy();
        }
        this.enemyDisplays = [];
        // Clear treasure display references
        this.treasureCounterText = null;
        this.treasureTurnsText = null;
    }

    // Update enemy HP display (call when enemy takes damage)
    private updateEnemyHP(display: EnemyDisplay): void {
        const { enemy, hpBarFill, hpText, hpBarWidth, hpBarHeight, scale } = display;
        const hpBarY = 267 * scale / 2 + 15;

        this.drawHPBar(hpBarFill, enemy.currentHP, enemy.maxHP, hpBarWidth, hpBarHeight, hpBarY);
        hpText.setText(`${enemy.currentHP}/${enemy.maxHP}`);
    }

    // Update enemy countdown display (call after each turn)
    private updateEnemyCountdown(display: EnemyDisplay): void {
        const minCooldown = Math.min(...display.enemy.actions.map(a => a.currentCooldown));
        display.countdownText.setText(minCooldown.toString());

        // Flash red when about to attack
        if (minCooldown <= 1) {
            display.countdownText.setColor('#ff0000');
        } else {
            display.countdownText.setColor('#ff6666');
        }
    }

    private createPlayerArea(): void {
        const playerAreaTop = this.TOP_UI_HEIGHT + this.ENEMY_AREA_HEIGHT;
        const playerAreaBottom = playerAreaTop + this.PLAYER_AREA_HEIGHT;
        const centerX = this.SPACE_WIDTH / 2;

        // Calculate module size
        const totalModules = 5;
        const moduleSpacing = 10;
        const padding = 20;
        const availableWidth = this.SPACE_WIDTH - (padding * 2);
        const moduleSize = Math.floor((availableWidth - (totalModules - 1) * moduleSpacing) / totalModules);

        // Layout constants
        const shieldBarHeight = 36;
        const shieldLabelHeight = 28;
        const shipHeight = 240;  // Actual ship height at scale 0.9 (267 * 0.9)
        const elementGap = 16;
        const topPadding = 12;

        // 1. Fitted modules row - at TOP of player area
        let currentY = playerAreaTop + topPadding;
        const moduleY = currentY + moduleSize / 2;
        this.createFittedModulesRow(centerX, moduleY);
        currentY += moduleSize + elementGap;

        // 2. Shield bar directly below modules
        const shieldY = currentY + shieldLabelHeight;
        this.createPlayerShieldBar(centerX, shieldY);
        const shieldBarBottom = shieldY + shieldBarHeight;

        // 3. Player ship - centered between shield bar and bottom of player area
        const shipAreaTop = shieldBarBottom + 20;  // Small gap below shield bar
        const shipAreaBottom = playerAreaBottom - 20;  // Small gap above bottom UI
        const shipY = shipAreaTop + (shipAreaBottom - shipAreaTop) / 2;
        this.createPlayerShip(centerX, shipY);
    }

    private createFittedModulesRow(centerX: number, y: number): void {
        const totalModules = 5;
        const spacing = 10;
        const padding = 20;
        const availableWidth = this.SPACE_WIDTH - (padding * 2);
        const moduleSize = Math.floor((availableWidth - (totalModules - 1) * spacing) / totalModules);
        const totalWidth = totalModules * moduleSize + (totalModules - 1) * spacing;
        const startX = centerX - totalWidth / 2 + moduleSize / 2;

        // Store for later use
        this.moduleSize = moduleSize;
        this.moduleStartX = startX;
        this.moduleY = y;
        this.combatModules = [];

        // Color mapping for orb colors to hex
        const orbColorToHex: Record<string, number> = {
            R: 0xe74c3c, B: 0x3498db, G: 0x2ecc71, Y: 0xf1c40f, P: 0x9b59b6,
        };

        for (let i = 0; i < totalModules; i++) {
            const fm = this.fittedModules[i];
            const x = startX + i * (moduleSize + spacing);

            if (fm && fm.module) {
                const combatModule = this.createCombatModuleCard(fm, x, y, moduleSize, orbColorToHex);
                if (combatModule) {
                    this.combatModules.push(combatModule);
                }
            } else {
                // Empty slot indicator
                const empty = this.add.graphics();
                empty.lineStyle(2, 0x333355, 0.6);
                empty.strokeRoundedRect(x - moduleSize / 2, y - moduleSize / 2, moduleSize, moduleSize, 6);
                this.spaceContainer.add(empty);
            }
        }
    }

    private createCombatModuleCard(
        fm: FittedModule,
        x: number,
        y: number,
        size: number,
        orbColorToHex: Record<string, number>
    ): CombatModuleDisplay | null {
        if (!fm.module) return null;

        const def = getModuleDefinition(fm.module.definitionId);
        if (!def) return null;

        // Get instance aspects (rolled per module) for damage type and charge sources
        const moduleAspects = fm.module.aspects || [];

        // Determine charge properties based on module type
        let chargeCost = 0;
        let chargeSources: string[] = [];
        let chargePerOrb = 1;
        let baseDamage = 0;
        let damageType = moduleAspects[0] || 'THERMAL';
        let weaponKey = '';  // Weapon type for multiplier lookup (laser, missile, etc.)
        let hasCharging = false;

        if (def.slotType === 'weapon') {
            // Weapons use passiveEffects params for base values
            const weaponEffect = def.passiveEffects?.find(e => e.effectId === 'eff_weapon_fire');
            const params = weaponEffect?.params as {
                chargeCost?: number;
                chargePerOrb?: number;
                baseDamage?: number;
                weaponKey?: string;
            } | undefined;

            const baseChargeCost = params?.chargeCost || 10;
            // Charge sources come from module's aspects (converted to orb colors)
            chargeSources = moduleAspects.map(a => ASPECT_TO_ORB[a] || 'R');
            chargePerOrb = params?.chargePerOrb || 1;
            baseDamage = params?.baseDamage || def.scaling?.baseDamage || 10;
            // Damage type is the first aspect of the module instance
            damageType = moduleAspects[0] || 'THERMAL';
            weaponKey = params?.weaponKey || 'laser';
            // Apply charge cost multiplier from support modules (keyed by weapon type)
            const costMultiplier = this.weaponChargeCostMultipliers[weaponKey] || 1.0;
            const costReduction = this.mutatorEffects.get('charge_cost_reduction') || 0;
            chargeCost = Math.max(1, Math.round(baseChargeCost * costMultiplier - costReduction));
            hasCharging = true;
        } else if (def.slotType === 'support' && def.skill) {
            // Supports with skill property charge using skill params
            const baseChargeCost = def.skill.chargeCost || 10;
            const scaling = def.scaling as Record<string, number> | undefined;
            const chargeCostPerLevel = scaling?.chargeCostPerLevel || 0;
            const levelBonus = fm.module.level - 1;
            const costReduction = this.mutatorEffects.get('charge_cost_reduction') || 0;
            chargeCost = Math.max(1, Math.round(baseChargeCost + chargeCostPerLevel * levelBonus - costReduction));
            // Support charge sources also come from module's aspects
            chargeSources = moduleAspects.map(a => ASPECT_TO_ORB[a] || 'R');
            chargePerOrb = def.skill.chargePerOrb || 1;
            hasCharging = true;
        }
        // Supports without skill are passive-only, no charging

        const primaryColor = chargeSources.length > 0
            ? (orbColorToHex[chargeSources[0]] || 0x888888)
            : 0x888888;

        // Create container
        const container = this.add.container(x, y);
        this.spaceContainer.add(container);

        // Card background with rarity border
        const rarityColors: Record<string, number> = {
            common: 0x555555, rare: 0x3498db, officer: 0x9b59b6,
        };
        const borderColor = rarityColors[def.rarity] || 0x555555;

        const bg = this.add.graphics();
        bg.fillStyle(0x1a1a2e, 1);
        bg.fillRoundedRect(-size / 2, -size / 2, size, size, 6);
        bg.lineStyle(2, borderColor, 1);
        bg.strokeRoundedRect(-size / 2, -size / 2, size, size, 6);
        // Inner panel
        bg.fillStyle(0x16161f, 1);
        bg.fillRoundedRect(-size / 2 + 4, -size / 2 + 4, size - 8, size - 8, 4);
        container.add(bg);

        // Draw the actual module symbol
        const symbolGraphics = this.add.graphics();
        const symbolSize = size * 0.55;
        const symbolColor = def.slotType === 'weapon' ? 0xcccccc : 0x88ccff;
        const symbolY = hasCharging ? -size * 0.08 : 0;
        this.drawModuleSymbol(symbolGraphics, def.symbolId, symbolSize, symbolColor, 0, symbolY);
        container.add(symbolGraphics);

        // Aspect gems in upper right corner (using instance aspects)
        if (moduleAspects.length > 0) {
            const gemSize = Math.max(10, size * 0.14);
            const gemSpacing = 2;
            const startX = size / 2 - gemSize / 2 - 4;
            const startY = -size / 2 + gemSize / 2 + 4;

            for (let i = 0; i < Math.min(moduleAspects.length, 2); i++) {
                const aspect = moduleAspects[i] as keyof typeof ASPECT_COLORS;
                const gemX = startX - i * (gemSize + gemSpacing);
                const gemColor = ASPECT_COLORS[aspect] || 0x888888;

                const gem = this.add.graphics();
                // Outer glow
                gem.fillStyle(gemColor, 0.3);
                gem.fillCircle(gemX, startY, gemSize / 2 + 2);
                // Main gem
                gem.fillStyle(gemColor, 1);
                gem.fillCircle(gemX, startY, gemSize / 2);
                // Highlight
                gem.fillStyle(0xffffff, 0.3);
                gem.fillCircle(gemX - 1, startY - 1, gemSize / 4);
                container.add(gem);
            }
        }

        // For passive-only supports, show "PASSIVE" label
        if (!hasCharging) {
            const passiveLabel = this.add.text(0, size / 2 - 16, 'PASSIVE', {
                fontFamily: 'monospace',
                fontSize: '12px',
                color: '#888888',
            }).setOrigin(0.5);
            container.add(passiveLabel);
        }

        // Charge bar at bottom (only for modules with charging)
        const barHeight = 10;
        const barWidth = size - 16;
        const barY = size / 2 - barHeight - 8;

        let chargeBarBg: Phaser.GameObjects.Graphics;
        let chargeBarFill: Phaser.GameObjects.Graphics;
        let multiplierText: Phaser.GameObjects.Text;

        if (hasCharging) {
            chargeBarBg = this.add.graphics();
            chargeBarBg.fillStyle(0x222233, 1);
            chargeBarBg.fillRoundedRect(-barWidth / 2, barY, barWidth, barHeight, 3);
            container.add(chargeBarBg);

            chargeBarFill = this.add.graphics();
            container.add(chargeBarFill);

            // Multiplier text (hidden initially) - large gold outline for visibility over icon
            multiplierText = this.add.text(0, barY - 16, '', {
                fontFamily: 'monospace',
                fontSize: '24px',
                fontStyle: 'bold',
                color: '#ffffff',
                stroke: '#000000',
                strokeThickness: 6,
                shadow: { offsetX: 0, offsetY: 0, color: '#d4a500', blur: 8, fill: false, stroke: true },
            }).setOrigin(0.5, 1);
            multiplierText.setVisible(false);
            container.add(multiplierText);
        } else {
            // Create dummy graphics for passive modules (won't be used)
            chargeBarBg = this.add.graphics();
            chargeBarFill = this.add.graphics();
            multiplierText = this.add.text(0, 0, '').setVisible(false);
        }

        return {
            fittedModule: fm,
            container,
            chargeBarBg,
            chargeBarFill,
            multiplierText,
            chargeCost,
            chargeSources,
            chargePerOrb,
            currentCharge: 0,
            displayedCharge: 0,
            displayedFillCount: 0,
            fillCount: 0,
            primaryColor,
            baseDamage,
            damageType,
            weaponKey,
            originalY: y,
        };
    }

    private updateModuleChargeBar(module: CombatModuleDisplay): void {
        // Skip passive modules (no charging)
        if (module.chargeCost <= 0) return;

        const size = this.moduleSize;
        const barHeight = 10;
        const barWidth = size - 16;
        const barY = size / 2 - barHeight - 8;

        module.chargeBarFill.clear();

        // Use displayed values for rendering (for smooth animation)
        const chargePercent = Math.min(module.displayedCharge / module.chargeCost, 1);
        const fillWidth = barWidth * chargePercent;

        if (fillWidth > 0) {
            // Darken color based on displayed fill count (stacking effect)
            let color = module.primaryColor;
            for (let i = 0; i < module.displayedFillCount; i++) {
                // Darken by 20% for each previous fill
                const r = ((color >> 16) & 0xff) * 0.7;
                const g = ((color >> 8) & 0xff) * 0.7;
                const b = (color & 0xff) * 0.7;
                color = (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
            }

            module.chargeBarFill.fillStyle(color, 1);
            module.chargeBarFill.fillRoundedRect(-barWidth / 2, barY, fillWidth, barHeight, 3);
        }

        // Update multiplier text based on actual fill count (show 1x so player knows it will fire)
        // Hide for weapons during treasure encounters (they won't attack)
        // Scale size based on multiplier: 1x=24px, 2x=28px, 3x=32px, etc.
        const isWeaponInTreasure = this.isTreasureEncounter && module.fittedModule.slotType === 'weapon';
        if (module.fillCount >= 1 && !isWeaponInTreasure) {
            const baseSize = 24;
            const scaledSize = baseSize + (module.fillCount - 1) * 4;
            module.multiplierText.setFontSize(scaledSize);
            module.multiplierText.setText(`${module.fillCount}x`);
            module.multiplierText.setVisible(true);
        } else {
            module.multiplierText.setVisible(false);
        }
    }

    private animateModuleChargeBar(module: CombatModuleDisplay): void {
        // Skip passive modules (no charging)
        if (module.chargeCost <= 0) return;

        // Calculate total charge including fills
        const totalCharge = module.currentCharge + (module.fillCount * module.chargeCost);
        const displayedTotal = module.displayedCharge + (module.displayedFillCount * module.chargeCost);

        // Create a tween target object
        const animTarget = { value: displayedTotal };

        this.tweens.add({
            targets: animTarget,
            value: totalCharge,
            duration: 250,
            ease: 'Cubic.easeOut',
            onUpdate: () => {
                // Convert back to charge + fillCount
                const fills = Math.floor(animTarget.value / module.chargeCost);
                const charge = animTarget.value - (fills * module.chargeCost);
                module.displayedCharge = charge;
                module.displayedFillCount = fills;
                this.updateModuleChargeBar(module);
            },
            onComplete: () => {
                // Ensure final values match
                module.displayedCharge = module.currentCharge;
                module.displayedFillCount = module.fillCount;
                this.updateModuleChargeBar(module);
            },
        });
    }

    private resetModuleCharges(): void {
        for (const module of this.combatModules) {
            module.currentCharge = 0;
            module.displayedCharge = 0;
            module.fillCount = 0;
            module.displayedFillCount = 0;
            this.updateModuleChargeBar(module);

            // Reset container position (in case recoil animation was interrupted)
            this.tweens.killTweensOf(module.container);
            module.container.y = module.originalY;
        }
    }

    private drawModuleSymbol(
        graphics: Phaser.GameObjects.Graphics,
        symbolId: string,
        size: number,
        color: number,
        offsetX: number,
        offsetY: number
    ): void {
        const symbol = getSymbol(symbolId);
        if (!symbol) {
            // Fallback: draw a simple shape
            graphics.lineStyle(2, color, 1);
            graphics.strokeCircle(offsetX, offsetY, size * 0.3);
            return;
        }

        for (const draw of symbol.draws) {
            this.executeSymbolDraw(graphics, draw, size, color, offsetX, offsetY);
        }
    }

    private executeSymbolDraw(
        graphics: Phaser.GameObjects.Graphics,
        draw: SymbolDraw,
        size: number,
        color: number,
        offsetX: number,
        offsetY: number
    ): void {
        const scale = size;

        switch (draw.type) {
            case 'line':
                graphics.lineStyle(draw.width || 2, color, 1);
                graphics.lineBetween(
                    offsetX + (draw.x1 || 0) * scale,
                    offsetY + (draw.y1 || 0) * scale,
                    offsetX + (draw.x2 || 0) * scale,
                    offsetY + (draw.y2 || 0) * scale
                );
                break;

            case 'circle':
                if (draw.fill) {
                    graphics.fillStyle(color, 1);
                    graphics.fillCircle(
                        offsetX + (draw.x || 0) * scale,
                        offsetY + (draw.y || 0) * scale,
                        (draw.radius || 0.1) * scale
                    );
                } else {
                    graphics.lineStyle(2, color, 1);
                    graphics.strokeCircle(
                        offsetX + (draw.x || 0) * scale,
                        offsetY + (draw.y || 0) * scale,
                        (draw.radius || 0.1) * scale
                    );
                }
                break;

            case 'rect':
                if (draw.fill === false) {
                    graphics.lineStyle(2, color, 1);
                    graphics.strokeRect(
                        offsetX + (draw.x || 0) * scale,
                        offsetY + (draw.y || 0) * scale,
                        (draw.w || 0.1) * scale,
                        (draw.h || 0.1) * scale
                    );
                } else {
                    graphics.fillStyle(color, 1);
                    graphics.fillRect(
                        offsetX + (draw.x || 0) * scale,
                        offsetY + (draw.y || 0) * scale,
                        (draw.w || 0.1) * scale,
                        (draw.h || 0.1) * scale
                    );
                }
                break;

            case 'arc':
                graphics.lineStyle(draw.width || 2, color, 1);
                graphics.beginPath();
                graphics.arc(
                    offsetX + (draw.x || 0) * scale,
                    offsetY + (draw.y || 0) * scale,
                    (draw.radius || 0.1) * scale,
                    draw.start || 0,
                    draw.end || Math.PI * 2
                );
                graphics.strokePath();
                break;

            case 'polygon':
                if (draw.points && draw.points.length >= 4) {
                    const points: number[] = [];
                    for (let i = 0; i < draw.points.length; i += 2) {
                        points.push(offsetX + draw.points[i] * scale);
                        points.push(offsetY + draw.points[i + 1] * scale);
                    }

                    if (draw.stroke) {
                        graphics.lineStyle(2, color, 1);
                        graphics.beginPath();
                        graphics.moveTo(points[0], points[1]);
                        for (let i = 2; i < points.length; i += 2) {
                            graphics.lineTo(points[i], points[i + 1]);
                        }
                        graphics.closePath();
                        graphics.strokePath();
                    } else {
                        graphics.fillStyle(color, 1);
                        graphics.beginPath();
                        graphics.moveTo(points[0], points[1]);
                        for (let i = 2; i < points.length; i += 2) {
                            graphics.lineTo(points[i], points[i + 1]);
                        }
                        graphics.closePath();
                        graphics.fillPath();
                    }
                }
                break;
        }
    }

    private createPlayerShieldBar(centerX: number, y: number): void {
        const barWidth = this.SPACE_WIDTH - 40;
        const barHeight = 36;

        // Store position for animations
        this.shieldBarX = centerX;
        this.shieldBarY = y;
        this.shieldBarWidth = barWidth;
        this.shieldBarHeight = barHeight;

        // Label above bar
        const label = this.add.text(centerX, y - 24, 'SHIELD', {
            fontFamily: 'monospace',
            fontSize: '24px',
            color: '#aaaacc',
            fontStyle: 'bold',
        });
        label.setOrigin(0.5);
        this.spaceContainer.add(label);

        // Background
        const bg = this.add.graphics();
        bg.fillStyle(0x222233, 1);
        bg.fillRoundedRect(centerX - barWidth / 2, y, barWidth, barHeight, 6);
        this.spaceContainer.add(bg);

        // Fill
        const fill = this.add.graphics();
        fill.fillStyle(0x3498db, 1);
        const fillWidth = barWidth * (this.playerShield / this.playerMaxShield);
        fill.fillRoundedRect(centerX - barWidth / 2, y, fillWidth, barHeight, 6);
        fill.setName('playerShieldFill');
        this.spaceContainer.add(fill);

        // Value text inside bar
        const value = this.add.text(centerX, y + barHeight / 2, `${this.playerShield}/${this.playerMaxShield}`, {
            fontFamily: 'monospace',
            fontSize: '24px',
            color: '#ffffff',
            fontStyle: 'bold',
        });
        value.setOrigin(0.5);
        value.setName('playerShieldText');
        this.spaceContainer.add(value);
    }

    private updatePlayerShieldBar(): void {
        const fill = this.spaceContainer.getByName('playerShieldFill') as Phaser.GameObjects.Graphics;
        const text = this.spaceContainer.getByName('playerShieldText') as Phaser.GameObjects.Text;

        if (fill) {
            fill.clear();
            fill.fillStyle(0x3498db, 1);
            const fillWidth = this.shieldBarWidth * (this.playerShield / this.playerMaxShield);
            fill.fillRoundedRect(
                this.shieldBarX - this.shieldBarWidth / 2,
                this.shieldBarY,
                Math.max(0, fillWidth),
                this.shieldBarHeight,
                6
            );
        }

        if (text) {
            text.setText(`${this.playerShield}/${this.playerMaxShield}`);
        }
    }

    private createPlayerShip(centerX: number, y: number): void {
        // Create ship with smaller scale to fit the narrow area
        this.shipSprite = new ShipSprite(this, this.SPACE_X + centerX, y, { scale: 0.9 });

        // Apply tinting based on fitted modules (same logic as BaseScene)
        const weaponModules = this.fittedModules.filter(fm => fm.slotType === 'weapon' && fm.module);
        const supportModules = this.fittedModules.filter(fm => fm.slotType === 'support' && fm.module);

        if (weaponModules.length > 0) {
            const firstWeapon = weaponModules[0].module;
            if (firstWeapon && firstWeapon.aspects && firstWeapon.aspects.length > 0) {
                const aspectColors: Record<string, number> = {
                    THERMAL: 0x4a2020, CRYO: 0x203a4a, RADIATION: 0x204a20, EMP: 0x4a4a20, VOID: 0x3a204a,
                };
                const color = aspectColors[firstWeapon.aspects[0]] || 0x2a2a40;
                this.shipSprite.setRegionTint('wings', color);
                this.shipSprite.setRegionTint('engines', color);
            }
        }

        if (supportModules.length > 0) {
            const firstSupport = supportModules[0].module;
            if (firstSupport && firstSupport.aspects && firstSupport.aspects.length > 0) {
                const aspectColors: Record<string, number> = {
                    THERMAL: 0x3a2828, CRYO: 0x283038, RADIATION: 0x283a28, EMP: 0x38382a, VOID: 0x302838,
                };
                const color = aspectColors[firstSupport.aspects[0]] || 0x1a1a2e;
                this.shipSprite.setRegionTint('hull', color);
            }
        }
    }

    private createBottomUI(): void {
        const areaY = this.TOP_UI_HEIGHT + this.ENEMY_AREA_HEIGHT + this.PLAYER_AREA_HEIGHT;
        const centerX = this.SPACE_WIDTH / 2;
        const centerY = areaY + this.BOTTOM_UI_HEIGHT / 2;

        // Dark background to cover starfield
        const bg = this.add.graphics();
        bg.fillStyle(0x0a0a12, 1);
        bg.fillRect(0, areaY, this.SPACE_WIDTH, this.BOTTOM_UI_HEIGHT);
        this.spaceContainer.add(bg);

        // Retreat button - danger action styling with proper contrast
        const buttonWidth = this.SPACE_WIDTH - 40;
        const buttonHeight = 48;  // Taller to fit 24px text

        const btnBg = this.add.graphics();
        btnBg.fillStyle(0x8b2020, 1);  // Dark red for danger action
        btnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);

        const btnLabel = this.add.text(0, 0, 'RETREAT', {
            fontFamily: 'monospace',
            fontSize: '24px',
            color: '#ffffff',  // White text for contrast (4.5:1+ on dark red)
            fontStyle: 'bold',
        });
        btnLabel.setOrigin(0.5);

        const button = this.add.container(centerX, centerY, [btnBg, btnLabel]);
        button.setSize(buttonWidth, buttonHeight);
        button.setInteractive({ useHandCursor: true });

        button.on('pointerover', () => {
            btnBg.clear();
            btnBg.fillStyle(0xa52828, 1);  // Lighter red on hover
            btnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
        });

        button.on('pointerout', () => {
            btnBg.clear();
            btnBg.fillStyle(0x8b2020, 1);
            btnBg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 8);
        });

        button.on('pointerdown', () => {
            this.scene.start('BaseScene');
        });

        this.spaceContainer.add(button);
    }

    // ==================== BOARD (LEFT 70%) ====================

    private initializeBoard(): void {
        const { BOARD_COLS, BOARD_ROWS } = GAME_CONFIG;
        this.board = [];
        this.orbs = [];

        for (let row = 0; row < BOARD_ROWS; row++) {
            this.board[row] = [];
            this.orbs[row] = [];
            for (let col = 0; col < BOARD_COLS; col++) {
                this.board[row][col] = this.getRandomOrbColor();
                this.orbs[row][col] = null;
            }
        }

        this.resolveInitialMatches();
    }

    private getRandomOrbColor(): OrbColor {
        // Start with base weights and apply spawn biases
        const weights: Record<OrbColor, number> = { ...GAME_CONFIG.ORB_SPAWN_WEIGHTS };

        for (const bias of this.spawnBiases) {
            weights[bias.color] = Math.max(0, weights[bias.color] + bias.weightAdd);
        }

        const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
        let random = Math.random() * totalWeight;

        for (const [color, weight] of Object.entries(weights)) {
            random -= weight;
            if (random <= 0) {
                return color as OrbColor;
            }
        }
        return 'R';
    }

    private decrementSpawnBiases(): void {
        // Called after each board refill (cascade/gravity resolution)
        this.spawnBiases = this.spawnBiases.filter(bias => {
            bias.refillsRemaining--;
            return bias.refillsRemaining > 0;
        });
    }

    private isCellLocked(col: number, row: number): boolean {
        return this.lockedCells.some(cell => cell.col === col && cell.row === row);
    }

    private addLockedCells(count: number, durationTurns: number): void {
        const { BOARD_COLS, BOARD_ROWS } = GAME_CONFIG;
        const availableCells: { col: number; row: number }[] = [];

        // Find cells that aren't already locked
        for (let row = 0; row < BOARD_ROWS; row++) {
            for (let col = 0; col < BOARD_COLS; col++) {
                if (!this.isCellLocked(col, row)) {
                    availableCells.push({ col, row });
                }
            }
        }

        // Shuffle and pick random cells
        for (let i = availableCells.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [availableCells[i], availableCells[j]] = [availableCells[j], availableCells[i]];
        }

        const cellsToLock = availableCells.slice(0, Math.min(count, availableCells.length));

        for (const cell of cellsToLock) {
            this.lockedCells.push({ ...cell, turnsRemaining: durationTurns });
            this.drawLockedCellIndicator(cell.col, cell.row);
        }
    }

    private drawLockedCellIndicator(col: number, row: number): void {
        const { ORB_SIZE, ORB_SPACING } = GAME_CONFIG;
        const cellSize = ORB_SIZE + ORB_SPACING;
        const key = `${col},${row}`;

        // Remove existing indicator if any
        const existing = this.lockedCellGraphics.get(key);
        if (existing) existing.destroy();

        // Create lock indicator
        const indicator = this.add.graphics();
        const x = col * cellSize + ORB_SIZE / 2;
        const y = row * cellSize + ORB_SIZE / 2;

        // Draw a pulsing red/dark overlay
        indicator.fillStyle(0x220000, 0.4);
        indicator.fillCircle(x, y, ORB_SIZE / 2);

        // Draw lock icon (simplified padlock)
        indicator.lineStyle(3, 0xff4444, 0.8);
        // Lock body
        indicator.strokeRect(x - 12, y - 5, 24, 18);
        // Lock shackle (arc)
        indicator.beginPath();
        indicator.arc(x, y - 8, 10, Math.PI, 0, false);
        indicator.strokePath();

        this.boardContainer.add(indicator);
        this.lockedCellGraphics.set(key, indicator);
    }

    private clearLockedCellIndicator(col: number, row: number): void {
        const key = `${col},${row}`;
        const indicator = this.lockedCellGraphics.get(key);
        if (indicator) {
            indicator.destroy();
            this.lockedCellGraphics.delete(key);
        }
    }

    private decrementLockedCells(): void {
        // Called at end of turn
        this.lockedCells = this.lockedCells.filter(cell => {
            cell.turnsRemaining--;
            if (cell.turnsRemaining <= 0) {
                this.clearLockedCellIndicator(cell.col, cell.row);
                return false;
            }
            return true;
        });
    }

    private resolveInitialMatches(): void {
        const { BOARD_COLS, BOARD_ROWS } = GAME_CONFIG;
        let hasMatches = true;
        let iterations = 0;
        const maxIterations = 100;

        while (hasMatches && iterations < maxIterations) {
            hasMatches = false;
            iterations++;

            for (let row = 0; row < BOARD_ROWS; row++) {
                for (let col = 0; col < BOARD_COLS; col++) {
                    if (this.wouldCreateMatch(col, row, this.board[row][col]!)) {
                        this.board[row][col] = this.getRandomOrbColor();
                        hasMatches = true;
                    }
                }
            }
        }
    }

    private wouldCreateMatch(col: number, row: number, color: OrbColor): boolean {
        if (col >= 2 && this.board[row][col - 1] === color && this.board[row][col - 2] === color) {
            return true;
        }
        if (row >= 2 && this.board[row - 1][col] === color && this.board[row - 2][col] === color) {
            return true;
        }
        return false;
    }

    private createBoardDisplay(): void {
        const { BOARD_COLS, BOARD_ROWS, ORB_SIZE, ORB_SPACING } = GAME_CONFIG;

        const cellSize = ORB_SIZE + ORB_SPACING;
        const naturalWidth = BOARD_COLS * cellSize - ORB_SPACING;
        const naturalHeight = BOARD_ROWS * cellSize - ORB_SPACING;

        // Fill the entire left area with minimal padding
        const edgePadding = 8;  // Tiny edge padding
        const timerBarSpace = 30;  // Space for timer bar below
        const maxWidth = this.BOARD_WIDTH - (edgePadding * 2);
        const maxHeight = GAME_CONFIG.HEIGHT - timerBarSpace - (edgePadding * 2);
        const scaleX = maxWidth / naturalWidth;
        const scaleY = maxHeight / naturalHeight;
        const scale = Math.min(scaleX, scaleY);  // Scale to fit, filling the space

        const boardWidth = naturalWidth;
        const boardHeight = naturalHeight;

        // Center in left area (should be nearly edge-to-edge now)
        const startX = (this.BOARD_WIDTH - boardWidth * scale) / 2;
        const startY = (GAME_CONFIG.HEIGHT - timerBarSpace - boardHeight * scale) / 2;

        this.boardContainer = this.add.container(startX, startY);
        this.boardContainer.setScale(scale);

        // Board background - minimal padding around cells
        const bg = this.add.graphics();
        bg.fillStyle(0x0a0a12, 1);
        bg.fillRoundedRect(-12, -12, boardWidth + 24, boardHeight + 24, 12);
        bg.fillStyle(0x12121a, 1);
        bg.fillRoundedRect(-8, -8, boardWidth + 16, boardHeight + 16, 8);
        this.boardContainer.add(bg);

        // Grid cells
        const grid = this.add.graphics();
        grid.lineStyle(1, 0x1a1a28, 0.6);
        for (let row = 0; row < BOARD_ROWS; row++) {
            for (let col = 0; col < BOARD_COLS; col++) {
                const x = col * cellSize;
                const y = row * cellSize;
                grid.strokeRoundedRect(x, y, ORB_SIZE, ORB_SIZE, 6);
            }
        }
        this.boardContainer.add(grid);

        // Create orbs
        this.createAllOrbs();
    }

    private createTimerBar(): void {
        const { BOARD_COLS, BOARD_ROWS, ORB_SIZE, ORB_SPACING } = GAME_CONFIG;

        const cellSize = ORB_SIZE + ORB_SPACING;
        const boardWidth = BOARD_COLS * cellSize - ORB_SPACING;
        const boardHeight = BOARD_ROWS * cellSize - ORB_SPACING;

        const scale = this.boardContainer.scaleX;
        // Timer bar matches board width (with minimal background padding)
        const barWidth = (boardWidth + 16) * scale;
        const barHeight = 18;
        const barX = this.boardContainer.x - 8 * scale;
        const barY = this.boardContainer.y + (boardHeight + 8) * scale + 8;

        this.timerBarBg = this.add.graphics();
        this.timerBarBg.fillStyle(0x1a1a2e, 1);
        this.timerBarBg.fillRoundedRect(barX, barY, barWidth, barHeight, 6);

        this.timerBarFill = this.add.graphics();
        this.timerBarFill.setData('barX', barX);
        this.timerBarFill.setData('barY', barY);
        this.timerBarFill.setData('barWidth', barWidth);
        this.timerBarFill.setData('barHeight', barHeight);

        this.timerBarFill.setAlpha(0);
        this.timerBarBg.setAlpha(0.3);
    }

    private updateTimerBar(): void {
        const barX = this.timerBarFill.getData('barX') as number;
        const barY = this.timerBarFill.getData('barY') as number;
        const barWidth = this.timerBarFill.getData('barWidth') as number;
        const barHeight = this.timerBarFill.getData('barHeight') as number;

        this.timerBarFill.clear();

        if (this.timerProgress > 0) {
            let color: number;
            if (this.timerProgress > 0.5) {
                color = 0x3498db;
            } else if (this.timerProgress > 0.25) {
                color = 0xf39c12;
            } else {
                color = 0xe74c3c;
            }

            this.timerBarFill.fillStyle(color, 1);
            this.timerBarFill.fillRoundedRect(barX, barY, barWidth * this.timerProgress, barHeight, 6);
        }
    }

    private startTimer(): void {
        const { MOVE_TIME_SECONDS } = GAME_CONFIG;
        // Apply move time bonus from effects, mutators, and debuff from enemies (minimum 1 second)
        const mutatorPenalty = this.mutatorEffects.get('move_time_penalty') || 0;
        const totalMoveTime = Math.max(1, MOVE_TIME_SECONDS + this.moveTimeBonus + mutatorPenalty - this.moveTimeDebuffAmount);

        this.timerProgress = 1;
        this.timerBarFill.setAlpha(1);
        this.timerBarBg.setAlpha(1);
        this.updateTimerBar();

        if (this.timerTween) {
            this.timerTween.stop();
        }

        this.timerTween = this.tweens.add({
            targets: this,
            timerProgress: 0,
            duration: totalMoveTime * 1000,
            ease: 'Linear',
            onUpdate: () => {
                this.updateTimerBar();
            },
            onComplete: () => {
                this.onTimerExpired();
            },
        });
    }

    private stopTimer(): void {
        if (this.timerTween) {
            this.timerTween.stop();
            this.timerTween = null;
        }

        // Reset timer to full and fade out
        this.timerProgress = 1;
        this.updateTimerBar();

        this.tweens.add({
            targets: [this.timerBarFill, this.timerBarBg],
            alpha: 0.3,
            duration: 200,
        });
    }

    private onTimerExpired(): void {
        if (!this.isDragging) return;
        this.endDrag();
    }

    private endDrag(): void {
        if (!this.isDragging) return;

        if (this.draggedOrb) {
            this.tweens.add({
                targets: this.draggedOrb.container,
                scaleX: 1,
                scaleY: 1,
                duration: 100,
                ease: 'Back.easeOut',
            });
        }

        const didSwap = this.swapOccurred;

        this.isDragging = false;
        this.draggedOrb = null;
        this.dragStartPos = null;
        this.currentDragPos = null;
        this.swapOccurred = false;

        // Stop timer regardless of whether a swap occurred
        this.stopTimer();

        // Only process turn if a swap actually occurred
        if (!didSwap) {
            return;
        }
        this.cascadeCount = 0;

        // Reset turn tracking (but NOT module charges - they persist across turns)
        this.turnMatchCounts = { R: 0, B: 0, G: 0, Y: 0, P: 0, S: 0 };

        this.checkAndResolveMatches();
    }

    private createAllOrbs(): void {
        const { BOARD_COLS, BOARD_ROWS } = GAME_CONFIG;

        for (let row = 0; row < BOARD_ROWS; row++) {
            for (let col = 0; col < BOARD_COLS; col++) {
                const color = this.board[row][col];
                if (color) {
                    this.createOrb(col, row, color);
                }
            }
        }
    }

    private createOrb(col: number, row: number, color: OrbColor): OrbData {
        const { ORB_SIZE, ORB_SPACING } = GAME_CONFIG;
        const cellSize = ORB_SIZE + ORB_SPACING;
        const x = col * cellSize + ORB_SIZE / 2;
        const y = row * cellSize + ORB_SIZE / 2;

        const container = this.add.container(x, y);

        // Layer order: glow (bottom) -> main graphics -> scanlines (top)
        const glowGraphics = this.add.graphics();
        glowGraphics.setBlendMode(Phaser.BlendModes.ADD);

        const graphics = this.add.graphics();

        const scanlineGraphics = this.add.graphics();
        scanlineGraphics.setAlpha(0.06); // Softened scanlines

        this.drawOrb(graphics, color, ORB_SIZE * 0.42);
        this.drawGlow(glowGraphics, color, ORB_SIZE * 0.42, false); // Subtle glow by default
        this.drawScanlines(scanlineGraphics, color, ORB_SIZE * 0.42);

        container.add([glowGraphics, graphics, scanlineGraphics]);
        this.boardContainer.add(container);

        const orbData: OrbData = { color, container, graphics, glowGraphics, scanlineGraphics };
        this.orbs[row][col] = orbData;

        return orbData;
    }

    // CRT Reactor Display Gem Colors
    private static readonly GEM_PALETTE: Record<OrbColor, { core: number; glow: number }> = {
        R: { core: 0xFF4A3D, glow: 0xB61F1A },
        B: { core: 0x2FA0FF, glow: 0x0F4C8A },
        G: { core: 0x2BFF9A, glow: 0x0F7A4F },
        Y: { core: 0xFFD24A, glow: 0x8A6A12 },
        P: { core: 0xB06CFF, glow: 0x4F1E86 },
        S: { core: 0xF2F6FF, glow: 0x7A8CA3 },
    };
    private static readonly BEZEL_FILL = 0x141821;
    private static readonly BEZEL_STROKE = 0x2A3140;
    private static readonly WINDOW_BORDER = 0x0B0E14;
    private static readonly GLYPH_COLOR = 0xD7DEE8;

    private drawOrb(graphics: Phaser.GameObjects.Graphics, color: OrbColor, radius: number): void {
        const palette = GameScene.GEM_PALETTE[color];
        const size = radius * 2.2; // Convert radius to full size
        const half = size / 2;
        const chamfer = size * 0.22; // Corner chamfer amount

        graphics.clear();

        // Draw chamfered octagon for weapon gems, rounded rect for shield
        if (color === 'S') {
            this.drawShieldGem(graphics, half, palette);
        } else {
            this.drawWeaponGem(graphics, half, chamfer, palette, color);
        }
    }

    private drawWeaponGem(
        graphics: Phaser.GameObjects.Graphics,
        half: number,
        chamfer: number,
        palette: { core: number; glow: number },
        color: OrbColor
    ): void {
        // Chamfered octagon vertices (clockwise from top-left corner)
        const getOctagonPoints = (h: number, c: number): number[] => [
            -h + c, -h,      // top edge start
            h - c, -h,       // top edge end
            h, -h + c,       // right edge start
            h, h - c,        // right edge end
            h - c, h,        // bottom edge start
            -h + c, h,       // bottom edge end
            -h, h - c,       // left edge start
            -h, -h + c,      // left edge end
        ];

        const outerPoints = getOctagonPoints(half, chamfer);
        const innerInset = half * 0.12;
        const innerHalf = half - innerInset;
        const innerChamfer = chamfer * 0.85;
        const innerPoints = getOctagonPoints(innerHalf, innerChamfer);

        // Layer 1: Outer bezel fill
        graphics.fillStyle(GameScene.BEZEL_FILL, 1);
        graphics.beginPath();
        graphics.moveTo(outerPoints[0], outerPoints[1]);
        for (let i = 2; i < outerPoints.length; i += 2) {
            graphics.lineTo(outerPoints[i], outerPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Bezel stroke
        graphics.lineStyle(2, GameScene.BEZEL_STROKE, 1);
        graphics.strokePath();

        // Inner shadow edge (darker rim inside bezel)
        graphics.lineStyle(1, 0x000000, 0.4);
        graphics.beginPath();
        graphics.moveTo(innerPoints[0], innerPoints[1]);
        for (let i = 2; i < innerPoints.length; i += 2) {
            graphics.lineTo(innerPoints[i], innerPoints[i + 1]);
        }
        graphics.closePath();
        graphics.strokePath();

        // Layer 2: Inner screen window (recessed display)
        const windowInset = half * 0.18;
        const windowHalf = half - windowInset;
        const windowChamfer = chamfer * 0.7;
        const windowPoints = getOctagonPoints(windowHalf, windowChamfer);

        graphics.fillStyle(GameScene.WINDOW_BORDER, 1);
        graphics.beginPath();
        graphics.moveTo(windowPoints[0], windowPoints[1]);
        for (let i = 2; i < windowPoints.length; i += 2) {
            graphics.lineTo(windowPoints[i], windowPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Layer 3: Screen emissive color
        const screenInset = half * 0.22;
        const screenHalf = half - screenInset;
        const screenChamfer = chamfer * 0.6;
        const screenPoints = getOctagonPoints(screenHalf, screenChamfer);

        graphics.fillStyle(palette.core, 0.9);
        graphics.beginPath();
        graphics.moveTo(screenPoints[0], screenPoints[1]);
        for (let i = 2; i < screenPoints.length; i += 2) {
            graphics.lineTo(screenPoints[i], screenPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Inset depth: inner shadow at top-left of screen
        const depthInset = screenHalf * 0.08;
        const depthHalf = screenHalf - depthInset;
        const depthChamfer = screenChamfer * 0.9;
        graphics.lineStyle(1.5, 0x000000, 0.35);
        graphics.beginPath();
        const depthPoints = getOctagonPoints(depthHalf, depthChamfer);
        // Only draw top-left portion for shadow
        graphics.moveTo(depthPoints[14], depthPoints[15]); // left edge end
        graphics.lineTo(depthPoints[0], depthPoints[1]);   // top-left corner
        graphics.lineTo(depthPoints[2], depthPoints[3]);   // top edge
        graphics.lineTo(depthPoints[4], depthPoints[5]);   // top-right corner start
        graphics.strokePath();

        // Rim highlight at bottom-right of screen
        graphics.lineStyle(1, 0xffffff, 0.12);
        graphics.beginPath();
        graphics.moveTo(depthPoints[6], depthPoints[7]);   // right edge end
        graphics.lineTo(depthPoints[8], depthPoints[9]);   // bottom-right corner
        graphics.lineTo(depthPoints[10], depthPoints[11]); // bottom edge
        graphics.lineTo(depthPoints[12], depthPoints[13]); // bottom-left corner start
        graphics.strokePath();

        // Layer 4: Etched glyph
        this.drawGlyph(graphics, color, screenHalf * 0.65);
    }

    private drawShieldGem(
        graphics: Phaser.GameObjects.Graphics,
        half: number,
        palette: { core: number; glow: number }
    ): void {
        const cornerRadius = half * 0.25;
        const w = half * 1.7;
        const h = half * 2;

        // Layer 1: Outer bezel (rounded rect) - double bezel for shield
        graphics.fillStyle(GameScene.BEZEL_FILL, 1);
        graphics.fillRoundedRect(-w / 2, -h / 2, w, h, cornerRadius);
        graphics.lineStyle(2, GameScene.BEZEL_STROKE, 1);
        graphics.strokeRoundedRect(-w / 2, -h / 2, w, h, cornerRadius);

        // Second bezel ring (double bezel effect)
        const bezel2Inset = half * 0.06;
        graphics.lineStyle(1.5, 0x3a4050, 0.8);
        graphics.strokeRoundedRect(
            -w / 2 + bezel2Inset, -h / 2 + bezel2Inset,
            w - bezel2Inset * 2, h - bezel2Inset * 2,
            cornerRadius * 0.9
        );

        // Inner shadow
        const innerInset = half * 0.12;
        graphics.lineStyle(1, 0x000000, 0.4);
        graphics.strokeRoundedRect(
            -w / 2 + innerInset, -h / 2 + innerInset,
            w - innerInset * 2, h - innerInset * 2,
            cornerRadius * 0.8
        );

        // Layer 2: Inner screen window
        const windowInset = half * 0.18;
        graphics.fillStyle(GameScene.WINDOW_BORDER, 1);
        graphics.fillRoundedRect(
            -w / 2 + windowInset, -h / 2 + windowInset,
            w - windowInset * 2, h - windowInset * 2,
            cornerRadius * 0.6
        );

        // Layer 3: Screen emissive color - cool white
        const screenInset = half * 0.24;
        const screenW = w - screenInset * 2;
        const screenH = h - screenInset * 2;
        const screenCorner = cornerRadius * 0.4;

        graphics.fillStyle(palette.core, 0.92);
        graphics.fillRoundedRect(
            -w / 2 + screenInset, -h / 2 + screenInset,
            screenW, screenH, screenCorner
        );

        // Cool white/blue inner glow gradient (center brighter)
        graphics.fillStyle(0xc8e0ff, 0.15);
        const glowInset = screenInset + half * 0.08;
        graphics.fillRoundedRect(
            -w / 2 + glowInset, -h / 2 + glowInset,
            w - glowInset * 2, h - glowInset * 2,
            screenCorner * 0.7
        );

        // Inset depth: top shadow
        graphics.lineStyle(1.5, 0x000000, 0.3);
        const depthY = -h / 2 + screenInset + 2;
        graphics.beginPath();
        graphics.moveTo(-w / 2 + screenInset + screenCorner, depthY);
        graphics.lineTo(w / 2 - screenInset - screenCorner, depthY);
        graphics.strokePath();

        // Inset depth: bottom highlight
        graphics.lineStyle(1, 0xffffff, 0.1);
        const highlightY = h / 2 - screenInset - 2;
        graphics.beginPath();
        graphics.moveTo(-w / 2 + screenInset + screenCorner, highlightY);
        graphics.lineTo(w / 2 - screenInset - screenCorner, highlightY);
        graphics.strokePath();

        // Layer 4: Bold plus sign glyph for shield
        this.drawShieldGlyph(graphics, half * 0.55);
    }

    private drawShieldGlyph(graphics: Phaser.GameObjects.Graphics, size: number): void {
        // Bold etched + glyph with slight glow effect
        const pSize = size * 0.8;
        const thickness = size * 0.18;

        // Outer glow/shadow
        graphics.fillStyle(0x8090a8, 0.25);
        graphics.fillRect(-thickness / 2 - 1, -pSize - 1, thickness + 2, pSize * 2 + 2);
        graphics.fillRect(-pSize - 1, -thickness / 2 - 1, pSize * 2 + 2, thickness + 2);

        // Main + bars
        graphics.fillStyle(GameScene.GLYPH_COLOR, 0.55);
        graphics.fillRect(-thickness / 2, -pSize, thickness, pSize * 2);
        graphics.fillRect(-pSize, -thickness / 2, pSize * 2, thickness);

        // Inner highlight
        graphics.fillStyle(0xffffff, 0.15);
        graphics.fillRect(-thickness / 2 + 1, -pSize + 1, thickness - 2, pSize * 2 - 2);
        graphics.fillRect(-pSize + 1, -thickness / 2 + 1, pSize * 2 - 2, thickness - 2);
    }

    private drawGlyph(graphics: Phaser.GameObjects.Graphics, color: OrbColor, size: number): void {
        // Increased stroke width ~25% (2 -> 2.5) and alpha (0.4 -> 0.55) for better readability
        graphics.lineStyle(2.5, GameScene.GLYPH_COLOR, 0.55);

        switch (color) {
            case 'R': // Lightning bolt zigzag
                const lw = size * 0.5;
                const lh = size * 0.9;
                graphics.beginPath();
                graphics.moveTo(lw * 0.3, -lh);
                graphics.lineTo(-lw * 0.2, -lh * 0.15);
                graphics.lineTo(lw * 0.25, -lh * 0.1);
                graphics.lineTo(-lw * 0.3, lh);
                graphics.strokePath();
                break;

            case 'B': // Three wave lines (water)
                for (let i = -1; i <= 1; i++) {
                    const waveY = i * size * 0.35;
                    graphics.beginPath();
                    graphics.moveTo(-size * 0.6, waveY);
                    graphics.lineTo(-size * 0.3, waveY - size * 0.12);
                    graphics.lineTo(0, waveY);
                    graphics.lineTo(size * 0.3, waveY + size * 0.12);
                    graphics.lineTo(size * 0.6, waveY);
                    graphics.strokePath();
                }
                break;

            case 'G': // Triangle pointing up
                const triSize = size * 0.75;
                graphics.beginPath();
                graphics.moveTo(0, -triSize * 0.8);
                graphics.lineTo(triSize * 0.7, triSize * 0.5);
                graphics.lineTo(-triSize * 0.7, triSize * 0.5);
                graphics.closePath();
                graphics.strokePath();
                break;

            case 'Y': // Diamond (rotated square)
                const dSize = size * 0.7;
                graphics.beginPath();
                graphics.moveTo(0, -dSize);
                graphics.lineTo(dSize * 0.7, 0);
                graphics.lineTo(0, dSize);
                graphics.lineTo(-dSize * 0.7, 0);
                graphics.closePath();
                graphics.strokePath();
                break;

            case 'P': // Hexagon outline
                const hexSize = size * 0.65;
                graphics.beginPath();
                for (let i = 0; i < 6; i++) {
                    const angle = (Math.PI / 3) * i - Math.PI / 2;
                    const x = Math.cos(angle) * hexSize;
                    const y = Math.sin(angle) * hexSize;
                    if (i === 0) graphics.moveTo(x, y);
                    else graphics.lineTo(x, y);
                }
                graphics.closePath();
                graphics.strokePath();
                break;

            case 'S': // Shield uses dedicated drawShieldGlyph instead
                break;
        }
    }

    private drawGlow(glowGraphics: Phaser.GameObjects.Graphics, color: OrbColor, radius: number, active = false): void {
        const palette = GameScene.GEM_PALETTE[color];
        const size = radius * 2.2;
        const half = size / 2;

        glowGraphics.clear();

        // Active glow for drag/match - strong halo
        // Default (inactive) - very subtle, almost invisible
        const intensity = active ? 1.0 : 0.15;
        const glowSteps = active ? 4 : 2;

        for (let i = glowSteps; i > 0; i--) {
            const expand = (i / glowSteps) * size * (active ? 0.25 : 0.1);
            const alpha = 0.15 * intensity * (1 - i / glowSteps);

            glowGraphics.fillStyle(palette.glow, alpha);

            if (color === 'S') {
                // Rounded rect glow for shield - cool tint when active
                const glowColor = active ? 0xa8c8ff : palette.glow;
                glowGraphics.fillStyle(glowColor, alpha);
                const w = half * 1.7 + expand * 2;
                const h = half * 2 + expand * 2;
                const cornerRadius = half * 0.25 + expand * 0.5;
                glowGraphics.fillRoundedRect(-w / 2, -h / 2, w, h, cornerRadius);
            } else {
                // Circle glow for weapons
                glowGraphics.fillCircle(0, 0, half + expand);
            }
        }

        // Core glow only when active
        if (active) {
            const coreColor = color === 'S' ? 0xc8e0ff : palette.glow;
            glowGraphics.fillStyle(coreColor, 0.25);
            if (color === 'S') {
                const w = half * 1.3;
                const h = half * 1.5;
                glowGraphics.fillRoundedRect(-w / 2, -h / 2, w, h, half * 0.15);
            } else {
                glowGraphics.fillCircle(0, 0, half * 0.7);
            }
        }
    }

    // Scanline offset for scrolling animation (shared across all orbs)
    private scanlineOffset = 0;
    private scanlineTimer?: Phaser.Time.TimerEvent;

    private startScanlineAnimation(): void {
        // Slow scroll ~1/3 original speed for subtle CRT effect
        this.scanlineTimer = this.time.addEvent({
            delay: 200, // Slowed from 66ms to 200ms (1/3 speed)
            callback: () => {
                this.scanlineOffset += 1;
                if (this.scanlineOffset >= 100) this.scanlineOffset = 0;

                // Redraw all orb scanlines
                const { BOARD_ROWS, BOARD_COLS, ORB_SIZE } = GAME_CONFIG;
                for (let row = 0; row < BOARD_ROWS; row++) {
                    for (let col = 0; col < BOARD_COLS; col++) {
                        const orb = this.orbs[row]?.[col];
                        if (orb?.scanlineGraphics) {
                            this.drawScanlines(orb.scanlineGraphics, orb.color, ORB_SIZE * 0.42);
                        }
                    }
                }
            },
            loop: true
        });
    }

    // Simple seeded random for consistent noise pattern
    private scanlineNoise(x: number, y: number): number {
        const n = Math.sin(x * 12.9898 + y * 78.233 + this.scanlineOffset * 0.1) * 43758.5453;
        return n - Math.floor(n);
    }

    private drawScanlines(scanlineGraphics: Phaser.GameObjects.Graphics, color: OrbColor, radius: number): void {
        const size = radius * 2.2;
        const half = size / 2;
        const chamfer = size * 0.22;

        scanlineGraphics.clear();

        // Softer scanlines with noise breakup
        const lineSpacing = 4;
        const screenInset = half * 0.22;
        const screenHalf = half - screenInset;

        // Draw scanlines with noise breakup
        const startY = -screenHalf + this.scanlineOffset % lineSpacing;

        for (let y = startY; y < screenHalf; y += lineSpacing) {
            let xBound: number;

            if (color === 'S') {
                const w = half * 1.7 - screenInset * 2;
                xBound = w / 2 * 0.9;
            } else {
                const absY = Math.abs(y);
                const screenChamfer = chamfer * 0.6;

                if (absY < screenHalf - screenChamfer) {
                    xBound = screenHalf * 0.95;
                } else {
                    const cornerDist = absY - (screenHalf - screenChamfer);
                    xBound = (screenHalf - cornerDist) * 0.95;
                }
            }

            if (xBound > 0) {
                // Draw line segments with noise gaps for breakup effect
                const segmentWidth = 6;
                for (let x = -xBound; x < xBound; x += segmentWidth) {
                    const noise = this.scanlineNoise(x, y);
                    // More breakup on shield gem, skip some segments randomly
                    const skipChance = color === 'S' ? 0.4 : 0.25;
                    if (noise > skipChance) {
                        const alpha = 0.6 + noise * 0.4; // Vary opacity
                        scanlineGraphics.lineStyle(1, 0x000000, alpha);
                        scanlineGraphics.beginPath();
                        const endX = Math.min(x + segmentWidth, xBound);
                        scanlineGraphics.moveTo(x, y);
                        scanlineGraphics.lineTo(endX, y);
                        scanlineGraphics.strokePath();
                    }
                }
            }
        }
    }

    private getOrbPosition(col: number, row: number): { x: number; y: number } {
        const { ORB_SIZE, ORB_SPACING } = GAME_CONFIG;
        const cellSize = ORB_SIZE + ORB_SPACING;
        return {
            x: col * cellSize + ORB_SIZE / 2,
            y: row * cellSize + ORB_SIZE / 2,
        };
    }

    private setupInput(): void {
        this.input.on('pointerdown', this.onPointerDown, this);
        this.input.on('pointermove', this.onPointerMove, this);
        this.input.on('pointerup', this.onPointerUp, this);
    }

    private getBoardPosition(pointer: Phaser.Input.Pointer): { col: number; row: number } | null {
        const { BOARD_COLS, BOARD_ROWS, ORB_SIZE, ORB_SPACING } = GAME_CONFIG;
        const cellSize = ORB_SIZE + ORB_SPACING;
        const scale = this.boardContainer.scaleX;

        const localX = (pointer.x - this.boardContainer.x) / scale;
        const localY = (pointer.y - this.boardContainer.y) / scale;

        const col = Math.floor(localX / cellSize);
        const row = Math.floor(localY / cellSize);

        if (col >= 0 && col < BOARD_COLS && row >= 0 && row < BOARD_ROWS) {
            return { col, row };
        }
        return null;
    }

    // Get board position clamped to edges - for drag tracking outside board bounds
    private getBoardPositionClamped(pointer: Phaser.Input.Pointer): { col: number; row: number } {
        const { BOARD_COLS, BOARD_ROWS, ORB_SIZE, ORB_SPACING } = GAME_CONFIG;
        const cellSize = ORB_SIZE + ORB_SPACING;
        const scale = this.boardContainer.scaleX;

        const localX = (pointer.x - this.boardContainer.x) / scale;
        const localY = (pointer.y - this.boardContainer.y) / scale;

        const col = Math.floor(localX / cellSize);
        const row = Math.floor(localY / cellSize);

        return {
            col: Math.max(0, Math.min(BOARD_COLS - 1, col)),
            row: Math.max(0, Math.min(BOARD_ROWS - 1, row)),
        };
    }

    private onPointerDown(pointer: Phaser.Input.Pointer): void {
        console.log('onPointerDown', { isAnimating: this.isAnimating, isGameOver: this.isGameOver, isChoosingPath: this.isChoosingPath });
        if (this.isAnimating || this.isGameOver || this.isChoosingPath) return;

        const pos = this.getBoardPosition(pointer);
        console.log('Board position:', pos, 'orb:', pos ? this.orbs[pos.row]?.[pos.col] : null);
        // Don't allow starting drag on a locked cell
        if (pos && this.isCellLocked(pos.col, pos.row)) return;
        if (pos && this.orbs[pos.row][pos.col]) {
            this.isDragging = true;
            this.dragStartPos = pos;
            this.currentDragPos = pos;
            this.draggedOrb = this.orbs[pos.row][pos.col];

            this.startTimer();

            if (this.draggedOrb) {
                this.boardContainer.bringToTop(this.draggedOrb.container);
                this.tweens.add({
                    targets: this.draggedOrb.container,
                    scaleX: 1.15,
                    scaleY: 1.15,
                    duration: 80,
                    ease: 'Back.easeOut',
                });
            }
        }
    }

    private onPointerMove(pointer: Phaser.Input.Pointer): void {
        if (!this.isDragging || !this.draggedOrb || !this.currentDragPos) return;

        // Use clamped position so dragging continues even when pointer leaves the board
        // This allows the player to drag along edges by moving mouse outside the board area
        const pos = this.getBoardPositionClamped(pointer);

        if (pos.col === this.currentDragPos.col && pos.row === this.currentDragPos.row) return;

        const dx = pos.col - this.currentDragPos.col;
        const dy = pos.row - this.currentDragPos.row;
        if (Math.abs(dx) + Math.abs(dy) !== 1) return;

        // Don't allow swapping with a locked cell
        if (this.isCellLocked(pos.col, pos.row)) return;

        const fromPos = this.currentDragPos;
        const toPos = pos;

        this.performSwap(fromPos, toPos);
        this.currentDragPos = toPos;
    }

    private performSwap(from: { col: number; row: number }, to: { col: number; row: number }): void {
        const { ANIMATION } = GAME_CONFIG;

        this.swapOccurred = true; // Mark that a swap happened

        const orbA = this.orbs[from.row][from.col];
        const orbB = this.orbs[to.row][to.col];

        const tempColor = this.board[from.row][from.col];
        this.board[from.row][from.col] = this.board[to.row][to.col];
        this.board[to.row][to.col] = tempColor;

        this.orbs[from.row][from.col] = orbB;
        this.orbs[to.row][to.col] = orbA;

        if (orbB) {
            const targetPos = this.getOrbPosition(from.col, from.row);
            this.tweens.add({
                targets: orbB.container,
                x: targetPos.x,
                y: targetPos.y,
                duration: ANIMATION.SWAP_DURATION,
                ease: 'Back.easeOut',
            });
        }

        if (orbA) {
            const targetPos = this.getOrbPosition(to.col, to.row);
            orbA.container.setPosition(targetPos.x, targetPos.y);
        }
    }

    private onPointerUp(): void {
        this.endDrag();
    }

    // Calculate fill/heal value based on match length
    // 3 gems = 3, 4 gems = 5, 5 gems = 7, 6 gems = 9, 7+ gems = 12
    private getMatchBonus(matchLength: number): number {
        if (matchLength <= 3) return 3;
        if (matchLength >= 7) return 12;
        // 4->5, 5->7, 6->9
        return 3 + (matchLength - 3) * 2;
    }

    // Cascade multiplier: first cascade (cascadeCount=2) gets 2x, second gets 3x, etc.
    // Player's initial matches (cascadeCount=1) get no cascade bonus
    private getCascadeMultiplier(): number {
        if (this.cascadeCount <= 1) return 1;
        return this.cascadeCount; // 2x, 3x, 4x, etc.
    }

    private async checkAndResolveMatches(): Promise<void> {
        this.isAnimating = true;

        const matches = this.findMatches();
        if (matches.length === 0) {
            // No more matches - cascade complete, trigger combat phase
            this.isAnimating = false;
            await this.resolveTurn();
            return;
        }

        this.cascadeCount++;
        const speedMult = this.getSpeedMultiplier();

        // Track colors, positions, AND match bonuses before fading
        // Apply cascade multiplier: cascades from gravity get bonus damage/charge
        const cascadeMultiplier = this.getCascadeMultiplier();

        // Show cascade bonus indicator above board when multiplier is active
        if (cascadeMultiplier > 1) {
            this.showCascadeIndicator(cascadeMultiplier);
        }

        const matchData: Array<{ pos: { col: number; row: number }; color: OrbColor; bonus: number }> = [];
        const processedCells = new Set<string>();
        for (const match of matches) {
            const baseBonus = this.getMatchBonus(match.length);
            const matchBonus = baseBonus * cascadeMultiplier;
            const bonusPerOrb = matchBonus / match.length; // Distribute bonus across orbs
            for (const pos of match) {
                const key = `${pos.col},${pos.row}`;
                if (processedCells.has(key)) continue;
                processedCells.add(key);

                const color = this.board[pos.row][pos.col];
                if (color) {
                    matchData.push({ pos, color, bonus: bonusPerOrb });
                    this.turnMatchCounts[color]++;
                }
            }
        }

        await this.animateMatchFade(matches, speedMult);

        // Generate particles from match positions to modules
        await this.animateMatchParticles(matchData);

        // Apply charges to modules
        this.applyCharges(matchData);

        // Clear matched cells - use a Set to ensure each cell is only cleared once
        const clearedCells = new Set<string>();
        for (const match of matches) {
            for (const pos of match) {
                const key = `${pos.col},${pos.row}`;
                if (clearedCells.has(key)) continue;
                clearedCells.add(key);

                this.board[pos.row][pos.col] = null;
                if (this.orbs[pos.row][pos.col]) {
                    this.orbs[pos.row][pos.col]!.container.destroy();
                    this.orbs[pos.row][pos.col] = null;
                }
            }
        }

        await this.animateGravity(speedMult);
        await this.animateSpawn(speedMult);
        this.decrementSpawnBiases(); // Decrement spawn biases after each refill
        await this.checkAndResolveMatches();
    }

    private applyCharges(matchData: Array<{ pos: { col: number; row: number }; color: OrbColor; bonus: number }>): void {
        // Group matches by color, summing up bonus values
        const colorBonuses: Record<string, number> = {};
        for (const { color, bonus } of matchData) {
            colorBonuses[color] = (colorBonuses[color] || 0) + bonus;
        }

        // Map orb colors to aspects
        const orbToAspect: Record<string, string> = {
            R: 'THERMAL', B: 'CRYO', G: 'RADIATION', Y: 'EMP', P: 'VOID',
        };

        // For treasure encounters, count treasure from all matches
        if (this.isTreasureEncounter) {
            let treasureGain = 0;

            for (const [orbColor, bonus] of Object.entries(colorBonuses)) {
                if (orbColor === 'S') continue; // Shield doesn't add to treasure

                // Base treasure from matching
                treasureGain += Math.round(bonus);

                // Bonus for modules with matching aspects (using instance aspects)
                const aspect = orbToAspect[orbColor];
                if (aspect) {
                    for (const fm of this.fittedModules) {
                        if (fm.module && fm.module.aspects?.includes(aspect)) {
                            treasureGain += Math.round(bonus * 0.5); // 50% bonus per matching module
                        }
                    }
                }
            }

            this.treasureCounter += treasureGain;
            this.updateTreasureDisplay();
        }

        // Separate weapons and supports
        const weapons = this.combatModules.filter(m => m.fittedModule.slotType === 'weapon');
        const supports = this.combatModules.filter(m => m.fittedModule.slotType === 'support');

        // Track support contributions for weapon bonus charging
        let supportBonusForWeapons = 0;

        // Apply charges to modules that use these colors
        for (const module of this.combatModules) {
            // Skip passive modules with no charging
            if (module.chargeCost <= 0 || module.chargeSources.length === 0) continue;

            for (const source of module.chargeSources) {
                const bonusTotal = colorBonuses[source] || 0;
                if (bonusTotal > 0) {
                    const chargeGain = bonusTotal * module.chargePerOrb;
                    module.currentCharge += chargeGain;

                    // If this is a support module, track for weapon bonus (1/2 rounded up)
                    if (module.fittedModule.slotType === 'support') {
                        supportBonusForWeapons += Math.ceil(bonusTotal / 2);
                    }

                    // Check for bar fills
                    while (module.currentCharge >= module.chargeCost) {
                        module.currentCharge -= module.chargeCost;
                        module.fillCount++;
                    }

                    this.animateModuleChargeBar(module);
                }
            }
        }

        // Also check supports with aspects but no skill (they still contribute to weapons)
        for (const module of supports) {
            // If this support has no skill (chargeCost <= 0), it still has aspects
            if (module.chargeCost > 0) continue; // Already handled above

            const moduleAspects = module.fittedModule.module?.aspects || [];

            for (const aspect of moduleAspects) {
                // Find matching orb color
                const matchingColor = Object.entries(orbToAspect).find(([, a]) => a === aspect)?.[0];
                if (matchingColor) {
                    const bonusTotal = colorBonuses[matchingColor] || 0;
                    if (bonusTotal > 0) {
                        supportBonusForWeapons += Math.ceil(bonusTotal / 2);
                    }
                }
            }
        }

        // Apply support bonus to all weapons
        if (supportBonusForWeapons > 0 && weapons.length > 0) {
            for (const weapon of weapons) {
                if (weapon.chargeCost <= 0) continue;

                const chargeGain = supportBonusForWeapons * weapon.chargePerOrb;
                weapon.currentCharge += chargeGain;

                // Check for bar fills
                while (weapon.currentCharge >= weapon.chargeCost) {
                    weapon.currentCharge -= weapon.chargeCost;
                    weapon.fillCount++;
                }

                this.animateModuleChargeBar(weapon);
            }
        }
    }

    private async animateMatchParticles(
        matchData: Array<{ pos: { col: number; row: number }; color: OrbColor; bonus: number }>
    ): Promise<void> {
        const { ORB_SIZE, ORB_SPACING } = GAME_CONFIG;
        const cellSize = ORB_SIZE + ORB_SPACING;
        const scale = this.boardContainer.scaleX;

        const orbColorToHex: Record<string, number> = {
            R: 0xe74c3c, B: 0x3498db, G: 0x2ecc71, Y: 0xf1c40f, P: 0x9b59b6, S: 0x95a5a6,
        };

        // Map orb colors to aspects for support module matching
        const orbToAspect: Record<string, string> = {
            R: 'THERMAL', B: 'CRYO', G: 'RADIATION', Y: 'EMP', P: 'VOID',
        };

        const tweenPromises: Promise<void>[] = [];

        // Track which support modules received particles for secondary effects
        const supportModulesHit: Array<{ module: CombatModuleDisplay; color: string; bonus: number }> = [];

        // Group match positions by color for efficiency, summing bonuses
        const colorPositions: Record<string, Array<{ x: number; y: number }>> = {};
        const colorBonuses: Record<string, number> = {};
        for (const { pos, color, bonus } of matchData) {
            if (!colorPositions[color]) colorPositions[color] = [];
            colorBonuses[color] = (colorBonuses[color] || 0) + bonus;
            const localX = pos.col * cellSize + ORB_SIZE / 2;
            const localY = pos.row * cellSize + ORB_SIZE / 2;
            const worldX = this.boardContainer.x + localX * scale;
            const worldY = this.boardContainer.y + localY * scale;
            colorPositions[color].push({ x: worldX, y: worldY });
        }

        // Handle shield gems specially - send to shield bar
        if (colorPositions['S'] && colorPositions['S'].length > 0) {
            const shieldPositions = colorPositions['S'];
            const shieldBonus = Math.round((colorBonuses['S'] || shieldPositions.length) * GAME_CONFIG.SHIELD_PER_ORB);
            const avgX = shieldPositions.reduce((sum, p) => sum + p.x, 0) / shieldPositions.length;
            const avgY = shieldPositions.reduce((sum, p) => sum + p.y, 0) / shieldPositions.length;

            // Target is the shield bar
            const targetX = this.SPACE_X + this.shieldBarX;
            const targetY = this.shieldBarY + this.shieldBarHeight / 2;

            // Create shield particle (large, with glow)
            const particle = this.add.graphics();
            particle.fillStyle(0x3498db, 0.3);
            particle.fillCircle(0, 0, 32); // Outer glow
            particle.fillStyle(0x3498db, 1);
            particle.fillCircle(0, 0, 22);
            particle.lineStyle(3, 0x5dade2, 0.9);
            particle.strokeCircle(0, 0, 24);
            particle.setPosition(avgX, avgY);
            particle.setDepth(1000);

            // Animate to shield bar
            const shieldPromise = new Promise<void>(resolve => {
                this.tweens.add({
                    targets: particle,
                    x: targetX,
                    y: targetY,
                    scaleX: 0.8,
                    scaleY: 0.8,
                    duration: 350,
                    ease: 'Cubic.easeIn',
                    onComplete: () => {
                        particle.destroy();

                        // Apply healing with match bonus, shield gain multiplier, and heal block debuff
                        const healAmount = Math.round(shieldBonus * this.shieldGainMultiplier * this.healBlockMultiplier);
                        const oldShield = this.playerShield;
                        this.playerShield = Math.min(this.playerMaxShield, this.playerShield + healAmount);
                        console.log('Shield heal:', { shieldBonus, shieldGainMultiplier: this.shieldGainMultiplier, healBlockMultiplier: this.healBlockMultiplier, healAmount, oldShield, newShield: this.playerShield, maxShield: this.playerMaxShield });
                        this.updatePlayerShieldBar();

                        // Flash shield bar green briefly
                        const fill = this.spaceContainer.getByName('playerShieldFill') as Phaser.GameObjects.Graphics;
                        if (fill) {
                            // Temporarily change color to green, then back
                            fill.clear();
                            fill.fillStyle(0x2ecc71, 1);
                            const fillWidth = this.shieldBarWidth * (this.playerShield / this.playerMaxShield);
                            fill.fillRoundedRect(
                                this.shieldBarX - this.shieldBarWidth / 2,
                                this.shieldBarY,
                                Math.max(0, fillWidth),
                                this.shieldBarHeight,
                                6
                            );

                            this.time.delayedCall(150, () => {
                                this.updatePlayerShieldBar();
                            });
                        }

                        resolve();
                    },
                });
            });
            tweenPromises.push(shieldPromise);
        }

        // For each non-shield color, send particles to modules that use that color
        for (const [color, positions] of Object.entries(colorPositions)) {
            if (color === 'S') continue; // Already handled

            const aspect = orbToAspect[color];
            const colorBonus = colorBonuses[color] || positions.length;

            // Find modules with chargeSources OR support modules with matching aspects
            const targetModules = this.combatModules.filter(m => {
                // Has direct charge source for this color
                if (m.chargeCost > 0 && m.chargeSources.includes(color)) return true;

                // Support module with matching aspect (for secondary weapon charging)
                if (m.fittedModule.slotType === 'support' && m.fittedModule.module) {
                    if (m.fittedModule.module.aspects?.includes(aspect)) return true;
                }
                return false;
            });

            if (targetModules.length === 0) continue;

            // Use center of all positions for this color
            const avgX = positions.reduce((sum, p) => sum + p.x, 0) / positions.length;
            const avgY = positions.reduce((sum, p) => sum + p.y, 0) / positions.length;

            for (const targetModule of targetModules) {
                const targetX = this.SPACE_X + targetModule.container.x;
                const targetY = targetModule.container.y;

                // Track support modules for secondary particle effects
                if (targetModule.fittedModule.slotType === 'support') {
                    supportModulesHit.push({ module: targetModule, color, bonus: colorBonus });
                }

                // Create particle with matching color (large, with glow)
                const particleColor = orbColorToHex[color] || 0xffffff;
                const particle = this.add.graphics();
                particle.fillStyle(particleColor, 0.3);
                particle.fillCircle(0, 0, 28); // Outer glow
                particle.fillStyle(particleColor, 1);
                particle.fillCircle(0, 0, 18);
                particle.lineStyle(2, 0xffffff, 0.5);
                particle.strokeCircle(0, 0, 20);
                particle.setPosition(avgX, avgY);
                particle.setDepth(1000);

                // Animate to module
                const promise = new Promise<void>(resolve => {
                    this.tweens.add({
                        targets: particle,
                        x: targetX,
                        y: targetY,
                        scaleX: 0.6,
                        scaleY: 0.6,
                        alpha: 0.9,
                        duration: 300,
                        ease: 'Cubic.easeIn',
                        onComplete: () => {
                            particle.destroy();
                            resolve();
                        },
                    });
                });
                tweenPromises.push(promise);
            }
        }

        // Wait for all primary particles to complete
        await Promise.all(tweenPromises);

        // Secondary particle effects: support modules -> weapons
        if (supportModulesHit.length > 0) {
            const weapons = this.combatModules.filter(m => m.fittedModule.slotType === 'weapon');
            if (weapons.length > 0) {
                const secondaryPromises: Promise<void>[] = [];

                for (const { module: support, color } of supportModulesHit) {
                    const supportX = this.SPACE_X + support.container.x;
                    const supportY = support.container.y;

                    for (const weapon of weapons) {
                        const weaponX = this.SPACE_X + weapon.container.x;
                        const weaponY = weapon.container.y;

                        // Create secondary particle (medium size, with glow)
                        const particleColor = orbColorToHex[color] || 0xffffff;
                        const particle = this.add.graphics();
                        particle.fillStyle(particleColor, 0.25);
                        particle.fillCircle(0, 0, 20); // Outer glow
                        particle.fillStyle(particleColor, 0.9);
                        particle.fillCircle(0, 0, 12);
                        particle.lineStyle(2, 0xffffff, 0.4);
                        particle.strokeCircle(0, 0, 14);
                        particle.setPosition(supportX, supportY);
                        particle.setDepth(1000);

                        // Animate from support to weapon
                        const promise = new Promise<void>(resolve => {
                            this.tweens.add({
                                targets: particle,
                                x: weaponX,
                                y: weaponY,
                                scaleX: 0.5,
                                scaleY: 0.5,
                                alpha: 0.6,
                                duration: 250,
                                ease: 'Cubic.easeOut',
                                onComplete: () => {
                                    particle.destroy();
                                    resolve();
                                },
                            });
                        });
                        secondaryPromises.push(promise);
                    }
                }

                await Promise.all(secondaryPromises);
            }
        }
    }

    private async resolveTurn(): Promise<void> {
        if (this.isGameOver) return;

        // Decrement locked cells at START of turn resolution (after player drag phase)
        // This ensures locks applied by enemies last through the next drag phase
        this.decrementLockedCells();

        // Handle treasure encounter differently
        if (this.isTreasureEncounter) {
            await this.resolveTreasureTurn();
            return;
        }

        // 1. Player attack phase - weapons fire
        const weaponModules = this.combatModules.filter(
            m => m.fittedModule.slotType === 'weapon' && m.fillCount > 0
        );

        if (weaponModules.length > 0) {
            await this.executePlayerAttacks(weaponModules);

            // Consume weapon charges after attacking
            for (const weapon of weaponModules) {
                weapon.fillCount = 0;
                weapon.displayedFillCount = 0;
                this.updateModuleChargeBar(weapon);
            }
        }

        // 1.5. Support skill phase - cast ready support skills
        const supportModules = this.combatModules.filter(
            m => m.fittedModule.slotType === 'support' && m.fillCount > 0
        );

        if (supportModules.length > 0) {
            await this.executePlayerSkills(supportModules);

            // Consume support charges after casting
            for (const support of supportModules) {
                support.fillCount = 0;
                support.displayedFillCount = 0;
                this.updateModuleChargeBar(support);
            }
        }

        // Check if all enemies are dead
        const livingEnemies = this.enemyDisplays.filter(e => e.enemy.currentHP > 0);
        if (livingEnemies.length === 0) {
            // Encounter victory! Handle progression
            await this.handleEncounterVictory();
            return;
        }

        // 2. Enemy attack phase - surviving enemies attack
        await this.executeEnemyAttacks();

        // 3. Tick down turn-based buffs
        this.tickBuffs();

        // 4. Check for game over
        if (this.playerShield <= 0) {
            await this.showGameOver();
        }
    }

    private async resolveTreasureTurn(): Promise<void> {
        // Decrement turns remaining
        this.treasureTurnsRemaining--;
        this.updateTreasureDisplay();

        // Check if treasure encounter is complete
        if (this.treasureTurnsRemaining <= 0) {
            // Award treasure amount as scrap
            this.scrapEarned += this.treasureCounter;
            this.updateScrapDisplay();
            await this.showVictory();
        }
    }

    private async handleEncounterVictory(): Promise<void> {
        // Reset module charges for next encounter
        this.resetModuleCharges();

        // Check if this was the boss - run complete!
        if (this.currentEncounter?.type === 'boss') {
            await this.showVictory();
            return;
        }

        // Check if we need to show path choice (after positions 1 and 2)
        if (this.currentPosition === 1 || this.currentPosition === 2) {
            await this.showPathChoice();
            return;
        }

        // Otherwise, advance to next encounter
        await this.advanceToNextEncounter();
    }

    // Path choice overlay elements
    private pathChoiceOverlay: Phaser.GameObjects.Graphics | null = null;
    private pathChoiceElements: Phaser.GameObjects.GameObject[] = [];

    private async showPathChoice(): Promise<void> {
        this.isChoosingPath = true;

        // Start warp effect
        this.startWarpEffect();

        // Clear enemy area
        this.clearEnemyDisplays();

        const centerX = GAME_CONFIG.WIDTH / 2;
        const centerY = GAME_CONFIG.HEIGHT / 2;

        // Dark overlay (full screen)
        this.pathChoiceOverlay = this.add.graphics();
        this.pathChoiceOverlay.fillStyle(0x000000, 0.85);
        this.pathChoiceOverlay.fillRect(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
        this.pathChoiceOverlay.setDepth(900);
        this.pathChoiceOverlay.setAlpha(0);

        // "CHOOSE YOUR PATH" title
        const titleText = this.add.text(centerX, centerY - 140, 'CHOOSE YOUR PATH', {
            fontFamily: 'monospace',
            fontSize: '48px',
            fontStyle: 'bold',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 6,
        });
        titleText.setOrigin(0.5);
        titleText.setDepth(902);
        titleText.setAlpha(0);
        this.pathChoiceElements.push(titleText);

        // Decorative lines
        const lineTop = this.add.graphics();
        lineTop.fillStyle(0x888888, 0.8);
        lineTop.fillRect(centerX - 250, centerY - 90, 500, 3);
        lineTop.setDepth(901);
        lineTop.setAlpha(0);
        this.pathChoiceElements.push(lineTop);

        // Button spacing
        const buttonSpacing = 200;
        const buttonY = centerY + 30;

        // TREASURE button (left path)
        const treasureBtn = this.createPathButton(
            centerX - buttonSpacing,
            buttonY,
            'left',
            'TREASURE',
            'Extra loot & rewards',
            0xf1c40f
        );
        this.pathChoiceElements.push(treasureBtn);

        // BOSS button (right path)
        const bossBtn = this.createPathButton(
            centerX + buttonSpacing,
            buttonY,
            'right',
            'BOSS',
            'Pursue the boss',
            0xe74c3c
        );
        this.pathChoiceElements.push(bossBtn);

        // Animate in
        this.tweens.add({
            targets: this.pathChoiceOverlay,
            alpha: 1,
            duration: 300,
        });

        this.tweens.add({
            targets: titleText,
            alpha: 1,
            duration: 300,
            delay: 100,
        });

        this.tweens.add({
            targets: lineTop,
            alpha: 1,
            duration: 200,
            delay: 150,
        });

        this.tweens.add({
            targets: [treasureBtn, bossBtn],
            alpha: 1,
            scaleX: 1,
            scaleY: 1,
            duration: 400,
            delay: 200,
            ease: 'Back.easeOut',
        });
    }

    private createPathButton(
        x: number,
        y: number,
        direction: 'left' | 'right',
        label: string,
        subtitle: string,
        color: number
    ): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        container.setDepth(902);
        container.setAlpha(0);
        container.setScale(0.8);

        const btnWidth = 280;
        const btnHeight = 160;

        // Button background
        const bg = this.add.graphics();
        bg.fillStyle(0x1a1a2e, 1);
        bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 16);
        bg.lineStyle(4, color, 1);
        bg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 16);
        container.add(bg);

        // Glow effect behind
        const glow = this.add.graphics();
        glow.fillStyle(color, 0.2);
        glow.fillRoundedRect(-btnWidth / 2 - 8, -btnHeight / 2 - 8, btnWidth + 16, btnHeight + 16, 20);
        container.addAt(glow, 0);

        // Icon (arrow or symbol)
        const icon = this.add.graphics();
        icon.fillStyle(color, 1);
        if (direction === 'left') {
            // Treasure chest simplified shape
            icon.fillRect(-30, -20, 60, 40);
            icon.fillStyle(0xd4a017, 1);
            icon.fillRect(-25, -15, 50, 30);
            icon.fillStyle(color, 1);
            icon.fillCircle(0, 0, 8);
        } else {
            // Skull simplified shape
            icon.fillCircle(0, -5, 25);
            icon.fillRect(-15, 10, 30, 15);
            icon.fillStyle(0x1a1a2e, 1);
            icon.fillCircle(-10, -8, 6);
            icon.fillCircle(10, -8, 6);
            icon.fillRect(-8, 8, 4, 8);
            icon.fillRect(-2, 8, 4, 8);
            icon.fillRect(4, 8, 4, 8);
        }
        icon.setPosition(0, -25);
        container.add(icon);

        // Main label
        const labelText = this.add.text(0, 35, label, {
            fontFamily: 'monospace',
            fontSize: '32px',
            fontStyle: 'bold',
            color: '#ffffff',
        });
        labelText.setOrigin(0.5);
        container.add(labelText);

        // Subtitle
        const subtitleText = this.add.text(0, 62, subtitle, {
            fontFamily: 'monospace',
            fontSize: '16px',
            color: '#aaaaaa',
        });
        subtitleText.setOrigin(0.5);
        container.add(subtitleText);

        // Hit area for interaction
        const hitArea = this.add.rectangle(0, 0, btnWidth, btnHeight, 0x000000, 0);
        hitArea.setInteractive({ useHandCursor: true });
        container.add(hitArea);

        hitArea.on('pointerover', () => {
            bg.clear();
            bg.fillStyle(0x2a2a4e, 1);
            bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 16);
            bg.lineStyle(5, color, 1);
            bg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 16);
            glow.clear();
            glow.fillStyle(color, 0.4);
            glow.fillRoundedRect(-btnWidth / 2 - 12, -btnHeight / 2 - 12, btnWidth + 24, btnHeight + 24, 24);
            container.setScale(1.05);
        });

        hitArea.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(0x1a1a2e, 1);
            bg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 16);
            bg.lineStyle(4, color, 1);
            bg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 16);
            glow.clear();
            glow.fillStyle(color, 0.2);
            glow.fillRoundedRect(-btnWidth / 2 - 8, -btnHeight / 2 - 8, btnWidth + 16, btnHeight + 16, 20);
            container.setScale(1);
        });

        hitArea.on('pointerdown', () => {
            this.selectPath(direction);
        });

        return container;
    }

    private async selectPath(path: 'left' | 'right'): Promise<void> {
        this.isChoosingPath = false;
        this.currentPath = path;

        // Animate out the path choice overlay
        const elementsToFade = [...this.pathChoiceElements];
        if (this.pathChoiceOverlay) {
            elementsToFade.push(this.pathChoiceOverlay);
        }

        await new Promise<void>(resolve => {
            this.tweens.add({
                targets: elementsToFade,
                alpha: 0,
                duration: 250,
                onComplete: () => {
                    // Clean up
                    for (const el of this.pathChoiceElements) {
                        el.destroy();
                    }
                    this.pathChoiceElements = [];
                    if (this.pathChoiceOverlay) {
                        this.pathChoiceOverlay.destroy();
                        this.pathChoiceOverlay = null;
                    }
                    resolve();
                },
            });
        });

        // Clear the enemy container as well
        this.enemyContainer.removeAll(true);

        // Advance to next position
        await this.advanceToNextEncounter();
    }

    private async advanceToNextEncounter(): Promise<void> {
        // Start warp if not already warping
        if (!this.isWarping) {
            this.startWarpEffect();
        }

        // Wait for warp effect
        await new Promise(r => setTimeout(r, 800));

        // Advance position
        this.currentPosition++;

        // Find the next encounter on current path
        let nextEncounter = this.runPlan?.encounters.find(
            e => e.position === this.currentPosition && e.path === this.currentPath
        );

        // If on right (boss) path and no encounter found, check for boss at position 5 (center)
        if (!nextEncounter && this.currentPath === 'right' && this.currentPosition === 5) {
            nextEncounter = this.runPlan?.encounters.find(
                e => e.position === 5 && e.type === 'boss'
            );
        }

        if (!nextEncounter) {
            // End of path - victory!
            await this.showVictory();
            return;
        }

        await this.loadEncounter(nextEncounter);
    }

    private async loadEncounter(encounter: Encounter): Promise<void> {
        this.currentEncounter = encounter;

        // Stop warp effect
        this.stopWarpEffect();

        if (encounter.type === 'treasure') {
            // Start treasure encounter
            this.isTreasureEncounter = true;
            this.treasureCounter = 0;
            this.treasureTurnsRemaining = 5;
            this.showTreasureDisplay();
        } else if (encounter.type === 'boss' && this.runPlan?.boss) {
            // Generate boss encounter using runPlan.boss
            const bossEncounter = generateBossEncounter(this.runPlan.boss, this.threatLevel);
            this.currentEncounter = bossEncounter;
            // Show boss announcement, then render
            await this.showBossAnnouncement(this.runPlan.boss);
            this.renderBoss(this.runPlan.boss);
        } else {
            // Regular combat encounter
            this.renderEnemies(encounter.enemies);
        }

        // Reset charges for new encounter
        this.resetModuleCharges();
    }

    private async showBossAnnouncement(boss: import('../data/EnemyData').BossTemplate): Promise<void> {
        const centerX = GAME_CONFIG.WIDTH / 2;
        const centerY = GAME_CONFIG.HEIGHT / 2;
        const aspect = boss.aspects[0] || 'VOID';
        const bossColor = ASPECT_COLORS[aspect as ElementType] || 0xff4444;

        // Dark overlay
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.7);
        overlay.fillRect(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
        overlay.setDepth(900);
        overlay.setAlpha(0);

        // Warning lines (top and bottom)
        const warningLineTop = this.add.graphics();
        warningLineTop.fillStyle(bossColor, 0.8);
        warningLineTop.fillRect(0, centerY - 120, GAME_CONFIG.WIDTH, 4);
        warningLineTop.setDepth(901);
        warningLineTop.setAlpha(0);

        const warningLineBottom = this.add.graphics();
        warningLineBottom.fillStyle(bossColor, 0.8);
        warningLineBottom.fillRect(0, centerY + 120, GAME_CONFIG.WIDTH, 4);
        warningLineBottom.setDepth(901);
        warningLineBottom.setAlpha(0);

        // "WARNING" text
        const warningText = this.add.text(centerX, centerY - 60, 'WARNING', {
            fontFamily: 'monospace',
            fontSize: '32px',
            fontStyle: 'bold',
            color: '#ff4444',
            stroke: '#000000',
            strokeThickness: 4,
        });
        warningText.setOrigin(0.5);
        warningText.setDepth(902);
        warningText.setAlpha(0);

        // "BOSS INCOMING" text
        const bossText = this.add.text(centerX, centerY, 'BOSS INCOMING', {
            fontFamily: 'monospace',
            fontSize: '48px',
            fontStyle: 'bold',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 6,
        });
        bossText.setOrigin(0.5);
        bossText.setDepth(902);
        bossText.setAlpha(0);
        bossText.setScale(0.5);

        // Boss name
        const nameText = this.add.text(centerX, centerY + 60, boss.name.toUpperCase(), {
            fontFamily: 'monospace',
            fontSize: '36px',
            fontStyle: 'bold',
            color: `#${bossColor.toString(16).padStart(6, '0')}`,
            stroke: '#000000',
            strokeThickness: 4,
        });
        nameText.setOrigin(0.5);
        nameText.setDepth(902);
        nameText.setAlpha(0);

        // Animate in
        await new Promise<void>(resolve => {
            // Fade in overlay
            this.tweens.add({
                targets: overlay,
                alpha: 1,
                duration: 200,
            });

            // Flash warning lines
            this.tweens.add({
                targets: [warningLineTop, warningLineBottom],
                alpha: 1,
                duration: 100,
                yoyo: true,
                repeat: 2,
            });

            // Warning text flashes
            this.tweens.add({
                targets: warningText,
                alpha: 1,
                duration: 150,
                delay: 100,
            });

            // Boss text slams in
            this.tweens.add({
                targets: bossText,
                alpha: 1,
                scaleX: 1,
                scaleY: 1,
                duration: 300,
                delay: 200,
                ease: 'Back.easeOut',
            });

            // Name fades in
            this.tweens.add({
                targets: nameText,
                alpha: 1,
                duration: 300,
                delay: 400,
                onComplete: () => {
                    // Hold for a moment, then fade out
                    this.time.delayedCall(800, () => {
                        this.tweens.add({
                            targets: [overlay, warningLineTop, warningLineBottom, warningText, bossText, nameText],
                            alpha: 0,
                            duration: 300,
                            onComplete: () => {
                                overlay.destroy();
                                warningLineTop.destroy();
                                warningLineBottom.destroy();
                                warningText.destroy();
                                bossText.destroy();
                                nameText.destroy();
                                resolve();
                            },
                        });
                    });
                },
            });
        });
    }

    private renderBoss(boss: import('../data/EnemyData').BossTemplate): void {
        this.clearEnemyDisplays();

        const enemyAreaTop = this.TOP_UI_HEIGHT;
        const centerX = this.SPACE_WIDTH / 2;
        // Position boss slightly higher to leave room for larger HP bar
        const centerY = enemyAreaTop + this.ENEMY_AREA_HEIGHT * 0.45;

        // Much larger scale for boss - nearly fills the enemy area
        const scale = 0.85;
        const aspect = boss.aspects[0] || 'VOID';
        const tintColor = ASPECT_DARK_COLORS[aspect];

        const multipliers = getThreatMultipliers(this.threatLevel);
        const maxHP = Math.round(boss.baseHP * multipliers.hpMult);

        // Create boss enemy display
        const generatedEnemy: GeneratedEnemy = {
            id: `boss_${boss.id}`,
            templateId: boss.id,
            name: boss.name,
            maxHP,
            currentHP: maxHP,
            resists: { ...boss.resists },
            actions: boss.actions.map((a, i) => {
                const cooldownReduction = this.mutatorEffects.get('enemy_cooldown_reduction') || 0;
                const reducedCooldown = Math.max(1, a.cooldown - cooldownReduction);
                return {
                    effectId: a.effectId,
                    cooldown: reducedCooldown,
                    currentCooldown: reducedCooldown + i,
                    params: { ...a.params },
                };
            }),
            aspects: [...boss.aspects],
        };

        // Larger HP bar for boss
        const hpBarWidth = 280;
        const hpBarHeight = 14;

        const display = this.createEnemyDisplay(
            generatedEnemy,
            centerX,
            centerY,
            scale,
            hpBarWidth,
            hpBarHeight,
            tintColor
        );
        this.enemyDisplays.push(display);

        // Add boss name label above the ship (above the countdown text)
        const bossNameLabel = this.add.text(0, -140 * scale - 55, boss.name, {
            fontFamily: 'monospace',
            fontSize: '28px',
            fontStyle: 'bold',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 3,
        });
        bossNameLabel.setOrigin(0.5);
        display.container.add(bossNameLabel);

        // Set up targeting for boss
        this.setupEnemyTargeting();
    }

    private showTreasureDisplay(): void {
        this.clearEnemyDisplays();

        const centerX = this.SPACE_WIDTH / 2;
        const centerY = this.TOP_UI_HEIGHT + this.ENEMY_AREA_HEIGHT / 2;

        // Create treasure container
        const container = this.add.container(centerX, centerY);
        this.enemyContainer.add(container);

        // Treasure icon (big golden circle)
        const treasureIcon = this.add.graphics();
        treasureIcon.fillStyle(0xf1c40f, 0.3);
        treasureIcon.fillCircle(0, 0, 80);
        treasureIcon.fillStyle(0xf1c40f, 1);
        treasureIcon.fillCircle(0, 0, 60);
        treasureIcon.fillStyle(0xffeaa7, 1);
        treasureIcon.fillCircle(-15, -15, 20);
        container.add(treasureIcon);

        // Treasure counter
        this.treasureCounterText = this.add.text(0, 0, '0', {
            fontFamily: 'monospace',
            fontSize: '48px',
            fontStyle: 'bold',
            color: '#2c3e50',
        });
        this.treasureCounterText.setOrigin(0.5);
        container.add(this.treasureCounterText);

        // Label
        const label = this.add.text(0, 90, 'TREASURE', {
            fontFamily: 'monospace',
            fontSize: '28px',
            fontStyle: 'bold',
            color: '#f1c40f',
        });
        label.setOrigin(0.5);
        container.add(label);

        // Turns remaining
        this.treasureTurnsText = this.add.text(0, 130, `${this.treasureTurnsRemaining} TURNS LEFT`, {
            fontFamily: 'monospace',
            fontSize: '24px',
            color: '#ffffff',
        });
        this.treasureTurnsText.setOrigin(0.5);
        container.add(this.treasureTurnsText);
    }

    private updateTreasureDisplay(): void {
        if (this.treasureCounterText) {
            this.treasureCounterText.setText(this.treasureCounter.toString());
        }
        if (this.treasureTurnsText) {
            this.treasureTurnsText.setText(`${this.treasureTurnsRemaining} TURNS LEFT`);
        }
    }

    private warpSpeedMultiplier = 1;
    private warpOverlay: Phaser.GameObjects.Graphics | null = null;

    private startWarpEffect(): void {
        this.isWarping = true;

        // Store original speeds if not already stored
        for (const star of this.stars) {
            if (!(star as any).originalSpeed) {
                (star as any).originalSpeed = star.speed;
            }
        }

        // Smoothly accelerate into warp
        this.tweens.add({
            targets: this,
            warpSpeedMultiplier: 25,
            duration: 400,
            ease: 'Cubic.easeIn',
            onUpdate: () => {
                // Update star speeds based on multiplier
                for (const star of this.stars) {
                    star.speed = ((star as any).originalSpeed || 1) * this.warpSpeedMultiplier;
                }
            },
        });
    }

    private stopWarpEffect(): void {
        // Smoothly decelerate out of warp
        this.tweens.add({
            targets: this,
            warpSpeedMultiplier: 1,
            duration: 500,
            ease: 'Cubic.easeOut',
            onUpdate: () => {
                // Update star speeds based on multiplier
                for (const star of this.stars) {
                    star.speed = ((star as any).originalSpeed || 1) * this.warpSpeedMultiplier;
                }
            },
            onComplete: () => {
                this.isWarping = false;

                // Restore original speeds
                for (const star of this.stars) {
                    star.speed = (star as any).originalSpeed || star.speed;
                }
            },
        });
    }

    private async showVictory(): Promise<void> {
        this.isGameOver = true;

        // Determine if this was a boss victory
        const isBossVictory = this.currentPath === 'right' && this.currentEncounter?.type === 'boss';
        const boss = isBossVictory ? this.runPlan?.boss : null;

        // Calculate boss rewards (only for logged-in players)
        let earnedBlueprint = false;
        let blueprintType: 'weapon' | 'support' | null = null;
        let rewardModule: ModuleDefinition | null = null;
        let rewardPlayerModule: PlayerModule | null = null;

        if (boss && !this.isGuest) {
            // Boss always drops a blueprint
            earnedBlueprint = true;
            blueprintType = boss.blueprintDrop === 'bp_weapon' ? 'weapon' : 'support';

            // Pick a random module matching boss type and aspects
            rewardModule = this.pickRandomModule(blueprintType, boss.aspects);

            // Create PlayerModule with rolled aspects (used for both saving and display)
            if (rewardModule) {
                rewardPlayerModule = {
                    definitionId: rewardModule.id,
                    instanceId: `${rewardModule.id}_${Date.now()}`,
                    level: 1,
                    aspects: rollAspectsForRarity(rewardModule.rarity),
                };
            }
        }

        // Save rewards (skip for guests)
        if (!this.isGuest) {
            // Save scrap earned (full amount for victory)
            if (this.scrapEarned > 0) {
                try {
                    await apiClient.updateScrap(this.scrapEarned);
                } catch (error) {
                    console.error('Failed to save scrap:', error);
                }
            }

            // Save blueprint earned
            if (earnedBlueprint && blueprintType) {
                try {
                    if (blueprintType === 'weapon') {
                        await apiClient.updateBlueprints(1, 0);
                    } else {
                        await apiClient.updateBlueprints(0, 1);
                    }
                } catch (error) {
                    console.error('Failed to save blueprint:', error);
                }
            }

            // Save module reward to inventory
            if (rewardPlayerModule) {
                try {
                    // Get current inventory, add new module, and save
                    const inventory = await apiClient.getInventory();
                    const updatedModules = [...inventory.modules, rewardPlayerModule];
                    await apiClient.saveInventory(updatedModules);
                } catch (error) {
                    console.error('Failed to save reward module:', error);
                }
            }
        }

        // Create dark overlay
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.8);
        overlay.fillRect(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
        overlay.setDepth(2000);

        // Calculate vertical positions based on content
        const centerY = GAME_CONFIG.HEIGHT / 2;
        let currentY = centerY - 140;

        // Victory text
        const victoryText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'SECTOR CLEARED', {
            fontFamily: 'monospace',
            fontSize: '64px',
            fontStyle: 'bold',
            color: '#2ecc71',
        });
        victoryText.setOrigin(0.5);
        victoryText.setDepth(2001);
        currentY += 70;

        if (this.isGuest) {
            // Guest mode: show sign-in prompt
            const guestText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'Progress not saved (Guest Mode)', {
                fontFamily: 'monospace',
                fontSize: '28px',
                color: '#ff8800',
            });
            guestText.setOrigin(0.5);
            guestText.setDepth(2001);
            currentY += 50;

            const signInPrompt = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'Sign in to save scrap, modules & blueprints!', {
                fontFamily: 'monospace',
                fontSize: '24px',
                color: '#ffffff',
            });
            signInPrompt.setOrigin(0.5);
            signInPrompt.setDepth(2001);
            currentY += 50;

            // Sign-in buttons
            this.createSignInButton(GAME_CONFIG.WIDTH / 2 - 140, currentY, 'Google', 0x4285F4, apiClient.getGoogleAuthUrl());
            this.createSignInButton(GAME_CONFIG.WIDTH / 2 + 140, currentY, 'Discord', 0x5865F2, apiClient.getDiscordAuthUrl());
            currentY += 60;
        } else {
            // Scrap earned display
            const scrapText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, `SCRAP: +${this.scrapEarned}`, {
                fontFamily: 'monospace',
                fontSize: '32px',
                fontStyle: 'bold',
                color: '#f1c40f',
            });
            scrapText.setOrigin(0.5);
            scrapText.setDepth(2001);
            currentY += 40;

            // Boss-specific rewards
            if (isBossVictory) {
                // Blueprint result
                if (earnedBlueprint && blueprintType) {
                    const bpText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY,
                        `BLUEPRINT: +1 ${blueprintType.toUpperCase()}`, {
                        fontFamily: 'monospace',
                        fontSize: '28px',
                        fontStyle: 'bold',
                        color: '#9b59b6',
                    });
                    bpText.setOrigin(0.5);
                    bpText.setDepth(2001);
                } else {
                    const noBpText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY,
                        'NO BLUEPRINT DROP', {
                        fontFamily: 'monospace',
                        fontSize: '24px',
                        color: '#666666',
                    });
                    noBpText.setOrigin(0.5);
                    noBpText.setDepth(2001);
                }
                currentY += 40;

                // Module reward - show as card
                if (rewardModule) {
                    const moduleLabel = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'MODULE REWARD', {
                        fontFamily: 'monospace',
                        fontSize: '20px',
                        color: '#aaaaaa',
                    });
                    moduleLabel.setOrigin(0.5);
                    moduleLabel.setDepth(2001);
                    currentY += 30;

                    // Use the rewardPlayerModule created earlier with rolled aspects
                    const cardSize = 100;
                    const moduleCard = this.moduleRenderer.createModuleCard(rewardPlayerModule!, {
                        size: cardSize,
                        showLevel: true,
                        showAffinities: true,
                    });

                    if (moduleCard) {
                        moduleCard.setPosition(GAME_CONFIG.WIDTH / 2, currentY + cardSize / 2);
                        moduleCard.setDepth(2001);
                    }

                    // Module name below card (card center is at currentY + cardSize/2, so bottom is at currentY + cardSize)
                    const rarityColors: Record<string, string> = {
                        common: '#aaaaaa',
                        rare: '#3498db',
                        officer: '#f1c40f',
                    };
                    const moduleName = this.add.text(GAME_CONFIG.WIDTH / 2, currentY + cardSize + 25,
                        rewardModule.name, {
                        fontFamily: 'monospace',
                        fontSize: '22px',
                        fontStyle: 'bold',
                        color: rarityColors[rewardModule.rarity] || '#ffffff',
                    });
                    moduleName.setOrigin(0.5);
                    moduleName.setDepth(2001);

                    currentY += cardSize + 60;
                }
            } else if (this.isTreasureEncounter) {
                // Treasure path message
                const treasureText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY,
                    `Treasure collected: ${this.treasureCounter}`, {
                    fontFamily: 'monospace',
                    fontSize: '24px',
                    color: '#aaaaaa',
                });
                treasureText.setOrigin(0.5);
                treasureText.setDepth(2001);
            }
        }

        // Back to Base button
        const buttonWidth = 280;
        const buttonHeight = 60;
        const buttonY = currentY + 60;

        const buttonBg = this.add.graphics();
        buttonBg.fillStyle(0x3498db, 1);
        buttonBg.fillRoundedRect(
            GAME_CONFIG.WIDTH / 2 - buttonWidth / 2,
            buttonY - buttonHeight / 2,
            buttonWidth,
            buttonHeight,
            8
        );
        buttonBg.setDepth(2001);

        const buttonText = this.add.text(GAME_CONFIG.WIDTH / 2, buttonY, 'Back to Base', {
            fontFamily: 'monospace',
            fontSize: '28px',
            fontStyle: 'bold',
            color: '#ffffff',
        });
        buttonText.setOrigin(0.5);
        buttonText.setDepth(2002);

        // Make button interactive
        const buttonZone = this.add.rectangle(
            GAME_CONFIG.WIDTH / 2,
            buttonY,
            buttonWidth,
            buttonHeight,
            0x000000,
            0
        );
        buttonZone.setDepth(2003);
        buttonZone.setInteractive({ useHandCursor: true });

        buttonZone.on('pointerover', () => {
            buttonBg.clear();
            buttonBg.fillStyle(0x5dade2, 1);
            buttonBg.fillRoundedRect(
                GAME_CONFIG.WIDTH / 2 - buttonWidth / 2,
                buttonY - buttonHeight / 2,
                buttonWidth,
                buttonHeight,
                8
            );
        });

        buttonZone.on('pointerout', () => {
            buttonBg.clear();
            buttonBg.fillStyle(0x3498db, 1);
            buttonBg.fillRoundedRect(
                GAME_CONFIG.WIDTH / 2 - buttonWidth / 2,
                buttonY - buttonHeight / 2,
                buttonWidth,
                buttonHeight,
                8
            );
        });

        buttonZone.on('pointerdown', () => {
            this.scene.start('BaseScene');
        });
    }

    private pickRandomModule(slotType: 'weapon' | 'support', aspects: ElementType[]): ModuleDefinition | null {
        const modulesData = getModulesData();
        if (!modulesData) return null;

        // Get treasureBudgetByRow for rarity (boss is at row 10, index 9, but budget is 0 for boss row)
        // Use the highest non-zero treasure budget (row 9 = index 8 which has budget 4)
        const budgets = getBudgetsForThreatLevel(this.threatLevel);
        let treasureBudget = budgets.treasure;

        // If budget is 0 (boss row), use highest available
        if (treasureBudget === 0) {
            treasureBudget = 4; // Officer tier for boss rewards
        }

        // Determine rarity based on treasureBudgetByRow:
        // 1 = common, 2 = rare, 3 = rare + 30% officer, 4 = officer
        let targetRarity: 'common' | 'rare' | 'officer';
        if (treasureBudget === 1) {
            targetRarity = 'common';
        } else if (treasureBudget === 2) {
            targetRarity = 'rare';
        } else if (treasureBudget === 3) {
            targetRarity = Math.random() < 0.3 ? 'officer' : 'rare';
        } else {
            targetRarity = 'officer';
        }

        // Filter modules by slot type and rarity
        // Note: Aspects are now rolled per instance, not per definition
        const matchingModules = modulesData.modules.filter(m =>
            m.slotType === slotType && m.rarity === targetRarity
        );

        if (matchingModules.length > 0) {
            return matchingModules[Math.floor(Math.random() * matchingModules.length)];
        }

        // Fallback: if no modules of target rarity, try any module of that slot type
        if (matchingModules.length === 0) {
            const fallbackModules = modulesData.modules.filter(m =>
                m.slotType === slotType
            );
            if (fallbackModules.length > 0) {
                return fallbackModules[Math.floor(Math.random() * fallbackModules.length)];
            }
            // Last resort: any module of that type
            const anyModules = modulesData.modules.filter(m => m.slotType === slotType);
            if (anyModules.length > 0) {
                return anyModules[Math.floor(Math.random() * anyModules.length)];
            }
            return null;
        }

        return matchingModules[Math.floor(Math.random() * matchingModules.length)];
    }

    private async executeEnemyAttacks(): Promise<void> {
        const livingEnemies = this.enemyDisplays.filter(e => e.enemy.currentHP > 0);

        for (const enemyDisplay of livingEnemies) {
            // Decrement all action cooldowns
            for (const action of enemyDisplay.enemy.actions) {
                action.currentCooldown--;
            }

            // Find actions ready to fire (cooldown <= 0)
            const readyActions = enemyDisplay.enemy.actions.filter(a => a.currentCooldown <= 0);

            for (const action of readyActions) {
                // Get effect type and dispatch appropriately
                const effectType = getEnemyEffectType(action.effectId);
                await this.executeEnemyEffect(enemyDisplay, effectType, action.params);

                // Reset cooldown
                action.currentCooldown = action.cooldown;
            }

            // Update countdown display
            this.updateEnemyCountdown(enemyDisplay);
        }
    }

    private async executeEnemyEffect(
        enemyDisplay: EnemyDisplay,
        effectType: string,
        params: Record<string, unknown>
    ): Promise<void> {
        switch (effectType) {
            case 'ENEMY_ATTACK': {
                const baseDamage = (params.baseDamage as number) || 10;
                const damageType = (params.damageType as string) || enemyDisplay.enemy.aspects[0] || 'VOID';

                // Apply enemy damage multiplier buff
                const buffedDamage = baseDamage * this.enemyDamageMultiplier;

                // Apply player resistance
                const playerResist = this.playerResists[damageType] || 0;
                // Apply glass cannon mutator (increases damage taken)
                const glassCannonMult = 1 + (this.mutatorEffects.get('glass_cannon_mult') || 0);
                const finalDamage = Math.round(buffedDamage * (1 - playerResist) * glassCannonMult);

                await this.animateEnemyAttack(enemyDisplay, finalDamage, damageType);
                break;
            }

            case 'DEBUFF_MOVE_TIME': {
                const seconds = (params.seconds as number) || 1;
                const durationTurns = (params.durationTurns as number) || 2;
                this.moveTimeDebuffAmount = Math.max(this.moveTimeDebuffAmount, seconds);
                this.moveTimeDebuffTurns = Math.max(this.moveTimeDebuffTurns, durationTurns);
                await this.showEnemyEffectText(enemyDisplay, `Move Time -${seconds}s`);
                break;
            }

            case 'BOARD_LOCK_CELLS': {
                const count = (params.count as number) || 3;
                const durationTurns = (params.durationTurns as number) || 2;
                this.addLockedCells(count, durationTurns);
                await this.showEnemyEffectText(enemyDisplay, `Locked ${count} cells!`);
                break;
            }

            case 'BOARD_CONVERT_RANDOM': {
                const count = (params.count as number) || 3;
                const toColor = (params.toColor as OrbColor) || 'R';
                await this.convertRandomOrbs(count, toColor);
                await this.showEnemyEffectText(enemyDisplay, `Converted ${count} orbs!`);
                break;
            }

            case 'BOARD_SPAWN_BIAS': {
                const color = (params.color as OrbColor) || 'R';
                const weightAdd = (params.weightAdd as number) || 10;
                const durationRefills = (params.durationRefills as number) || 3;
                this.spawnBiases.push({ color, weightAdd, refillsRemaining: durationRefills });
                await this.showEnemyEffectText(enemyDisplay, `${color} orb bias!`);
                break;
            }

            case 'SHIELD_DRAIN': {
                const amount = (params.amount as number) || 10;
                const oldShield = this.playerShield;
                this.playerShield = Math.max(0, this.playerShield - amount);
                console.log('Shield drain:', { amount, oldShield, newShield: this.playerShield });
                this.updatePlayerShieldBar();
                await this.showEnemyEffectText(enemyDisplay, `Drained ${amount} shield!`);
                break;
            }

            case 'DEBUFF_HEALING': {
                const multiplier = (params.multiplier as number) || 0.5;
                const durationTurns = (params.durationTurns as number) || 3;
                this.healBlockMultiplier = Math.min(this.healBlockMultiplier, multiplier);
                this.healBlockTurns = Math.max(this.healBlockTurns, durationTurns);
                await this.showEnemyEffectText(enemyDisplay, `Heal Block ${Math.round((1 - multiplier) * 100)}%!`);
                break;
            }

            case 'ENEMY_GAIN_RESIST': {
                const damageType = (params.damageType as string) || 'VOID';
                const resistAdd = (params.resistAdd as number) || 0.2;
                const durationTurns = (params.durationTurns as number) || 3;
                this.enemyResistBuffs.push({
                    enemyId: enemyDisplay.enemy.id,
                    damageType,
                    resistAdd,
                    turnsRemaining: durationTurns,
                });
                await this.showEnemyEffectText(enemyDisplay, `+${Math.round(resistAdd * 100)}% ${damageType} resist!`);
                break;
            }

            case 'ENEMY_DAMAGE_MULT': {
                const multiplierAdd = (params.multiplierAdd as number) || 0.25;
                const durationTurns = (params.durationTurns as number) || 3;
                this.enemyDamageMultiplier += multiplierAdd;
                this.enemyDamageMultiplierTurns = Math.max(this.enemyDamageMultiplierTurns, durationTurns);
                await this.showEnemyEffectText(enemyDisplay, `Enraged! +${Math.round(multiplierAdd * 100)}% dmg!`);
                break;
            }

            default:
                console.warn(`Unknown enemy effect type: ${effectType}`);
                break;
        }
    }

    private async showEnemyEffectText(enemyDisplay: EnemyDisplay, text: string): Promise<void> {
        // Show floating text near the enemy - accessibility: larger, longer, high contrast
        const x = this.SPACE_X + enemyDisplay.container.x;
        const y = enemyDisplay.container.y - 60;

        const effectText = this.add.text(x, y, text, {
            fontFamily: 'monospace',
            fontSize: '28px',
            fontStyle: 'bold',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 5,
            shadow: { offsetX: 2, offsetY: 2, color: '#ff4400', blur: 8, fill: false, stroke: true },
        });
        effectText.setOrigin(0.5);
        effectText.setDepth(600);

        // Hold visible for a moment before fading
        await new Promise<void>(resolve => {
            this.tweens.add({
                targets: effectText,
                y: y - 50,
                alpha: { from: 1, to: 0 },
                duration: 1800,
                ease: 'Cubic.easeIn',
                delay: 400, // Hold visible for 400ms before starting to fade
                onComplete: () => {
                    effectText.destroy();
                    resolve();
                },
            });
        });
    }

    private showCascadeIndicator(multiplier: number): void {
        // Show cascade bonus text above the board
        const x = this.boardContainer.x + this.boardContainer.width / 2;
        const y = this.boardContainer.y - 30;

        const cascadeText = this.add.text(x, y, `${multiplier}x CASCADE!`, {
            fontFamily: 'monospace',
            fontSize: '32px',
            fontStyle: 'bold',
            color: '#ffdd00',
            stroke: '#d4a500',
            strokeThickness: 4,
            shadow: { offsetX: 2, offsetY: 2, color: '#000000', blur: 6, fill: true },
        });
        cascadeText.setOrigin(0.5);
        cascadeText.setDepth(500);

        // Scale and fade animation
        cascadeText.setScale(0.5);
        this.tweens.add({
            targets: cascadeText,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 150,
            ease: 'Back.easeOut',
            onComplete: () => {
                this.tweens.add({
                    targets: cascadeText,
                    scaleX: 1,
                    scaleY: 1,
                    alpha: 0,
                    y: y - 40,
                    duration: 600,
                    ease: 'Cubic.easeOut',
                    onComplete: () => cascadeText.destroy(),
                });
            },
        });
    }

    private async convertRandomOrbs(count: number, toColor: OrbColor): Promise<void> {
        const { BOARD_COLS, BOARD_ROWS } = GAME_CONFIG;
        const availablePositions: { col: number; row: number }[] = [];

        // Find all positions with a different color (and not locked)
        for (let row = 0; row < BOARD_ROWS; row++) {
            for (let col = 0; col < BOARD_COLS; col++) {
                if (this.board[row][col] !== toColor && !this.isCellLocked(col, row)) {
                    availablePositions.push({ col, row });
                }
            }
        }

        // Shuffle and pick random positions
        for (let i = availablePositions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [availablePositions[i], availablePositions[j]] = [availablePositions[j], availablePositions[i]];
        }

        const positionsToConvert = availablePositions.slice(0, Math.min(count, availablePositions.length));

        // Convert the orbs with animation
        for (const pos of positionsToConvert) {
            this.board[pos.row][pos.col] = toColor;

            // Update visual
            const orb = this.orbs[pos.row][pos.col];
            if (orb) {
                orb.color = toColor;
                this.updateOrbGraphics(orb);

                // Flash effect
                this.tweens.add({
                    targets: orb.container,
                    scaleX: 1.3,
                    scaleY: 1.3,
                    duration: 100,
                    yoyo: true,
                    ease: 'Sine.easeInOut',
                });
            }
        }

        await new Promise(r => setTimeout(r, 200));
    }

    private updateOrbGraphics(orb: OrbData, active = false): void {
        const { ORB_SIZE } = GAME_CONFIG;
        // Use the same drawOrb function that createOrb uses to maintain consistent positioning
        this.drawOrb(orb.graphics, orb.color, ORB_SIZE * 0.42);
        if (orb.glowGraphics) {
            this.drawGlow(orb.glowGraphics, orb.color, ORB_SIZE * 0.42, active);
        }
        if (orb.scanlineGraphics) {
            this.drawScanlines(orb.scanlineGraphics, orb.color, ORB_SIZE * 0.42);
        }
    }

    private async animateEnemyAttack(enemy: EnemyDisplay, damage: number, damageType: string = 'VOID'): Promise<void> {
        const enemyX = this.SPACE_X + enemy.container.x;
        const enemyY = enemy.container.y;
        const playerX = this.shipSprite.container.x;
        const playerY = this.shipSprite.container.y;

        // Get color based on damage type
        const projectileColor = ASPECT_COLORS[damageType as keyof typeof ASPECT_COLORS] || 0xff4444;

        // Create enhanced projectile with glow
        const projectileContainer = this.add.container(enemyX, enemyY);
        projectileContainer.setDepth(500);

        // Outer glow layer
        const glow = this.add.graphics();
        glow.fillStyle(projectileColor, 0.3);
        glow.fillCircle(0, 0, 20);
        projectileContainer.add(glow);

        // Core projectile
        const projectile = this.add.graphics();
        projectile.fillStyle(projectileColor, 1);
        projectile.fillCircle(0, 0, 12);
        projectile.lineStyle(2, 0xffffff, 0.7);
        projectile.strokeCircle(0, 0, 14);
        // Inner highlight
        projectile.fillStyle(0xffffff, 0.5);
        projectile.fillCircle(-4, -4, 5);
        projectileContainer.add(projectile);

        // Enemy recoil animation
        this.tweens.add({
            targets: enemy.container,
            y: enemy.container.y - 12,
            duration: 60,
            yoyo: true,
            ease: 'Cubic.easeOut',
        });

        // Muzzle flash on enemy
        const muzzleFlash = this.add.graphics();
        muzzleFlash.fillStyle(projectileColor, 0.8);
        muzzleFlash.fillCircle(0, 0, 25);
        muzzleFlash.fillStyle(0xffffff, 0.6);
        muzzleFlash.fillCircle(0, 0, 12);
        muzzleFlash.setPosition(enemyX, enemyY);
        muzzleFlash.setDepth(499);

        this.tweens.add({
            targets: muzzleFlash,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 100,
            onComplete: () => muzzleFlash.destroy(),
        });

        // Trail particles during flight
        const trailParticles: Phaser.GameObjects.Graphics[] = [];
        const trailInterval = this.time.addEvent({
            delay: 30,
            callback: () => {
                const trail = this.add.graphics();
                trail.fillStyle(projectileColor, 0.5);
                trail.fillCircle(0, 0, 7);
                trail.setPosition(projectileContainer.x, projectileContainer.y);
                trail.setDepth(498);
                trailParticles.push(trail);

                this.tweens.add({
                    targets: trail,
                    alpha: 0,
                    scaleX: 0.3,
                    scaleY: 0.3,
                    duration: 180,
                    onComplete: () => {
                        trail.destroy();
                        const idx = trailParticles.indexOf(trail);
                        if (idx > -1) trailParticles.splice(idx, 1);
                    },
                });
            },
            loop: true,
        });

        // Animate projectile to player
        await new Promise<void>(resolve => {
            this.tweens.add({
                targets: projectileContainer,
                x: playerX,
                y: playerY,
                duration: 250,
                ease: 'Cubic.easeIn',
                onComplete: () => {
                    trailInterval.destroy();
                    projectileContainer.destroy();

                    // Hit explosion effect on player
                    this.createHitExplosion(playerX, playerY, projectileColor);

                    // Show damage number on player
                    this.showPlayerDamageNumber(playerX, playerY, damage, projectileColor);

                    // Shake player ship (more intense)
                    const originalX = this.shipSprite.container.x;
                    const originalY = this.shipSprite.container.y;
                    this.tweens.add({
                        targets: this.shipSprite.container,
                        x: originalX + 15,
                        y: originalY - 5,
                        duration: 40,
                        yoyo: true,
                        repeat: 2,
                        ease: 'Sine.easeInOut',
                        onComplete: () => {
                            this.shipSprite.container.x = originalX;
                            this.shipSprite.container.y = originalY;
                        },
                    });

                    // Apply damage to shield
                    this.playerShield = Math.max(0, this.playerShield - damage);
                    this.updatePlayerShieldBar();

                    // Flash shield bar red
                    const fill = this.spaceContainer.getByName('playerShieldFill') as Phaser.GameObjects.Graphics;
                    if (fill) {
                        this.tweens.add({
                            targets: fill,
                            alpha: 0.5,
                            duration: 100,
                            yoyo: true,
                        });
                    }

                    resolve();
                },
            });
        });

        // Small delay between attacks
        await new Promise(r => setTimeout(r, 150));
    }

    private showPlayerDamageNumber(x: number, y: number, damage: number, color: number): void {
        const damageText = this.add.text(x, y - 40, `-${damage}`, {
            fontFamily: 'monospace',
            fontSize: '32px',
            color: '#ff4444',
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 4,
        });
        damageText.setOrigin(0.5, 0.5);
        damageText.setDepth(600);

        // Pop-in effect
        damageText.setScale(0.5);

        this.tweens.add({
            targets: damageText,
            scaleX: 1.3,
            scaleY: 1.3,
            duration: 80,
            ease: 'Back.easeOut',
            onComplete: () => {
                this.tweens.add({
                    targets: damageText,
                    scaleX: 1,
                    scaleY: 1,
                    y: y - 100,
                    alpha: 0,
                    duration: 450,
                    ease: 'Cubic.easeOut',
                    onComplete: () => damageText.destroy(),
                });
            },
        });
    }

    private async showGameOver(): Promise<void> {
        this.isGameOver = true;

        // Save scrap earned (half amount, rounded up for defeat) - skip for guests
        const scrapToSave = Math.ceil(this.scrapEarned / 2);
        if (scrapToSave > 0 && !this.isGuest) {
            try {
                await apiClient.updateScrap(scrapToSave);
            } catch (error) {
                console.error('Failed to save scrap:', error);
            }
        }

        // Create dark overlay
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.8);
        overlay.fillRect(0, 0, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
        overlay.setDepth(2000);

        let currentY = GAME_CONFIG.HEIGHT / 2 - 120;

        // Game Over text
        const gameOverText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'RUN OVER', {
            fontFamily: 'monospace',
            fontSize: '64px',
            fontStyle: 'bold',
            color: '#ff4444',
        });
        gameOverText.setOrigin(0.5);
        gameOverText.setDepth(2001);
        currentY += 80;

        if (this.isGuest) {
            // Guest mode: show sign-in prompt instead of scrap
            const guestText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'Progress not saved (Guest Mode)', {
                fontFamily: 'monospace',
                fontSize: '28px',
                color: '#ff8800',
            });
            guestText.setOrigin(0.5);
            guestText.setDepth(2001);
            currentY += 50;

            const signInPrompt = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'Sign in to save your progress!', {
                fontFamily: 'monospace',
                fontSize: '24px',
                color: '#ffffff',
            });
            signInPrompt.setOrigin(0.5);
            signInPrompt.setDepth(2001);
            currentY += 50;

            // Sign-in buttons
            this.createSignInButton(GAME_CONFIG.WIDTH / 2 - 140, currentY, 'Google', 0x4285F4, apiClient.getGoogleAuthUrl());
            this.createSignInButton(GAME_CONFIG.WIDTH / 2 + 140, currentY, 'Discord', 0x5865F2, apiClient.getDiscordAuthUrl());
            currentY += 70;
        } else {
            // Scrap earned display (half, rounded up)
            const scrapText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, `SCRAP SALVAGED: +${scrapToSave}`, {
                fontFamily: 'monospace',
                fontSize: '36px',
                fontStyle: 'bold',
                color: '#f1c40f',
            });
            scrapText.setOrigin(0.5);
            scrapText.setDepth(2001);
            currentY += 50;

            // Subtext
            const subText = this.add.text(GAME_CONFIG.WIDTH / 2, currentY, 'Your ship was destroyed (50% scrap saved)', {
                fontFamily: 'monospace',
                fontSize: '28px',
                color: '#aaaaaa',
            });
            subText.setOrigin(0.5);
            subText.setDepth(2001);
            currentY += 70;
        }

        // Back to Base button
        const buttonWidth = 280;
        const buttonHeight = 60;
        const buttonY = currentY + 20;

        const buttonBg = this.add.graphics();
        buttonBg.fillStyle(0x3498db, 1);
        buttonBg.fillRoundedRect(
            GAME_CONFIG.WIDTH / 2 - buttonWidth / 2,
            buttonY - buttonHeight / 2,
            buttonWidth,
            buttonHeight,
            8
        );
        buttonBg.setDepth(2001);

        const buttonText = this.add.text(GAME_CONFIG.WIDTH / 2, buttonY, 'Back to Base', {
            fontFamily: 'monospace',
            fontSize: '28px',
            fontStyle: 'bold',
            color: '#ffffff',
        });
        buttonText.setOrigin(0.5);
        buttonText.setDepth(2002);

        // Make button interactive
        const buttonZone = this.add.rectangle(
            GAME_CONFIG.WIDTH / 2,
            buttonY,
            buttonWidth,
            buttonHeight,
            0x000000,
            0
        );
        buttonZone.setDepth(2003);
        buttonZone.setInteractive({ useHandCursor: true });

        buttonZone.on('pointerover', () => {
            buttonBg.clear();
            buttonBg.fillStyle(0x5dade2, 1);
            buttonBg.fillRoundedRect(
                GAME_CONFIG.WIDTH / 2 - buttonWidth / 2,
                buttonY - buttonHeight / 2,
                buttonWidth,
                buttonHeight,
                8
            );
        });

        buttonZone.on('pointerout', () => {
            buttonBg.clear();
            buttonBg.fillStyle(0x3498db, 1);
            buttonBg.fillRoundedRect(
                GAME_CONFIG.WIDTH / 2 - buttonWidth / 2,
                buttonY - buttonHeight / 2,
                buttonWidth,
                buttonHeight,
                8
            );
        });

        buttonZone.on('pointerdown', () => {
            this.scene.start('BaseScene');
        });
    }

    private createSignInButton(x: number, y: number, label: string, color: number, url: string): void {
        const buttonWidth = 200;
        const buttonHeight = 50;

        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 8);
        bg.setDepth(2001);

        const text = this.add.text(x, y, `Sign in with ${label}`, {
            fontFamily: 'monospace',
            fontSize: '18px',
            fontStyle: 'bold',
            color: '#ffffff',
        });
        text.setOrigin(0.5);
        text.setDepth(2002);

        const zone = this.add.rectangle(x, y, buttonWidth, buttonHeight, 0x000000, 0);
        zone.setDepth(2003);
        zone.setInteractive({ useHandCursor: true });

        zone.on('pointerover', () => {
            bg.clear();
            const lighterColor = this.lightenColor(color, 0.2);
            bg.fillStyle(lighterColor, 1);
            bg.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 8);
        });

        zone.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(color, 1);
            bg.fillRoundedRect(x - buttonWidth / 2, y - buttonHeight / 2, buttonWidth, buttonHeight, 8);
        });

        zone.on('pointerdown', () => {
            window.location.href = url;
        });
    }

    private lightenColor(color: number, amount: number): number {
        const r = Math.min(255, ((color >> 16) & 0xff) + Math.floor(255 * amount));
        const g = Math.min(255, ((color >> 8) & 0xff) + Math.floor(255 * amount));
        const b = Math.min(255, (color & 0xff) + Math.floor(255 * amount));
        return (r << 16) | (g << 8) | b;
    }

    private async executePlayerAttacks(weaponModules: CombatModuleDisplay[]): Promise<void> {
        // Get living enemies
        const livingEnemies = this.enemyDisplays.filter(e => e.enemy.currentHP > 0);
        if (livingEnemies.length === 0) return;

        // Start with the player's targeted enemy
        let currentTarget = this.getTargetedEnemy();
        if (!currentTarget) return;

        for (const weapon of weaponModules) {
            // Store initial fill count and attack that many times
            const attackCount = weapon.fillCount;
            for (let i = 0; i < attackCount; i++) {
                // Get current target (may have changed if previous target died)
                if (!currentTarget || currentTarget.enemy.currentHP <= 0) {
                    currentTarget = this.getTargetedEnemy();
                    if (!currentTarget) break; // No more living enemies
                }

                // Calculate damage before animation so we can display it
                const elementalMult = this.getElementalMultiplier(weapon.damageType, currentTarget.enemy.aspects);
                const baseResist = currentTarget.enemy.resists[weapon.damageType as keyof typeof currentTarget.enemy.resists] || 0;
                // Add temporary resist buffs from enemy effects
                const tempResistBuff = this.enemyResistBuffs
                    .filter(buff => buff.enemyId === currentTarget!.enemy.id && buff.damageType === weapon.damageType)
                    .reduce((sum, buff) => sum + buff.resistAdd, 0);
                const totalResist = baseResist + tempResistBuff;
                const resistMult = 1 - Math.min(0.6, totalResist); // Cap resist at 60%
                // Apply weapon damage multiplier from support modules (Heat Sink, Gyro Stabilizer, etc.)
                const dmgMultiplier = this.weaponDamageMultipliers[weapon.weaponKey] || 1.0;
                // Apply mutator damage bonuses
                const mutatorDamageMult = 1 + (this.mutatorEffects.get('damage_mult') || 0);
                const glassCannon = 1 + (this.mutatorEffects.get('glass_cannon_mult') || 0);
                const cascadeBonus = 1 + ((this.mutatorEffects.get('cascade_damage_bonus') || 0) * this.cascadeCount);
                const damage = Math.round(weapon.baseDamage * dmgMultiplier * elementalMult * resistMult * mutatorDamageMult * glassCannon * cascadeBonus);

                // Animate attack with damage number and effects
                const isEffective = elementalMult > 1.0;
                const isWeak = elementalMult < 1.0;
                await this.animateWeaponAttack(weapon, currentTarget, damage, isEffective, isWeak);

                // Apply damage
                currentTarget.enemy.currentHP = Math.max(0, currentTarget.enemy.currentHP - damage);
                this.updateEnemyHP(currentTarget);

                // Decrement fill count and update display to show countdown
                weapon.fillCount--;
                weapon.displayedFillCount = weapon.fillCount;
                this.updateModuleChargeBar(weapon);

                // Check for death
                if (currentTarget.enemy.currentHP <= 0) {
                    await this.animateEnemyDeath(currentTarget);
                    // Auto-target next living enemy
                    currentTarget = this.getTargetedEnemy();
                }
            }
        }

        // Recenter remaining enemies and update target highlights
        this.recenterEnemies();
        this.updateTargetHighlights();
    }

    private async executePlayerSkills(supportModules: CombatModuleDisplay[]): Promise<void> {
        for (const support of supportModules) {
            // Get module definition to find skill castEffects
            if (!support.fittedModule.module) continue;
            const def = getModuleDefinition(support.fittedModule.module.definitionId);
            if (!def || !def.skill || !def.skill.castEffects) continue;

            // Cast skill for each fillCount
            for (let i = 0; i < support.fillCount; i++) {
                // Animate skill cast
                await this.animateSkillCast(support);

                // Execute each cast effect
                for (const effect of def.skill.castEffects) {
                    await this.executePlayerSkillEffect(effect.effectId, effect.params, support);
                }
            }
        }
    }

    private async animateSkillCast(support: CombatModuleDisplay): Promise<void> {
        // Pulse effect on the support module
        const origScale = support.container.scaleX;

        await new Promise<void>(resolve => {
            this.tweens.add({
                targets: support.container,
                scaleX: origScale * 1.2,
                scaleY: origScale * 1.2,
                duration: 100,
                yoyo: true,
                ease: 'Cubic.easeOut',
                onComplete: () => {
                    // Flash effect
                    const flash = this.add.graphics();
                    flash.fillStyle(support.primaryColor, 0.5);
                    flash.fillCircle(0, 0, 40);
                    flash.setPosition(this.SPACE_X + support.container.x, support.container.y);
                    flash.setDepth(600);

                    this.tweens.add({
                        targets: flash,
                        alpha: 0,
                        scaleX: 2,
                        scaleY: 2,
                        duration: 200,
                        onComplete: () => {
                            flash.destroy();
                            resolve();
                        },
                    });
                },
            });
        });
    }

    private async executePlayerSkillEffect(
        effectId: string,
        params: Record<string, unknown>,
        support: CombatModuleDisplay
    ): Promise<void> {
        // Determine effect type from effectId
        if (effectId === 'eff_add_move_time') {
            const seconds = (params.seconds as number) || 0.5;
            const durationTurns = (params.durationTurns as number) || 2;
            this.moveTimeBonus += seconds;
            this.moveTimeBonusTurns = Math.max(this.moveTimeBonusTurns, durationTurns);
            await this.showSkillEffectText(support, `+${seconds}s Move Time`);
        } else if (effectId === 'eff_skill_convert_random') {
            const count = (params.count as number) || 5;
            const toColor = (params.toColor as OrbColor) || 'S';
            await this.convertRandomOrbs(count, toColor);
            await this.showSkillEffectText(support, `Converted ${count} to ${toColor}!`);
        } else if (effectId === 'eff_skill_lock_cells') {
            // Player skill that locks cells for enemies (beneficial)
            // This is different from enemy locking - we could show it helps maintain board state
            const count = (params.count as number) || 3;
            const durationTurns = (params.durationTurns as number) || 2;
            await this.showSkillEffectText(support, `Protected ${count} cells`);
            // Note: Player lock would need different implementation - skip for now
        } else if (effectId === 'eff_skill_loot_boost') {
            const rarityStepUp = (params.rarityStepUp as number) || 1;
            const durationNodes = (params.durationNodes as number) || 1;
            // Store loot boost state (would need to track this)
            await this.showSkillEffectText(support, `Loot Quality +${rarityStepUp}!`);
            // Note: Loot boost would need tracking - skip for now
        } else {
            console.warn(`Unknown player skill effect: ${effectId}`);
        }
    }

    private async showSkillEffectText(support: CombatModuleDisplay, text: string): Promise<void> {
        const x = this.SPACE_X + support.container.x;
        const y = support.container.y - 40;

        const effectText = this.add.text(x, y, text, {
            fontFamily: 'monospace',
            fontSize: '18px',
            fontStyle: 'bold',
            color: '#00ff88',
            stroke: '#000000',
            strokeThickness: 3,
        });
        effectText.setOrigin(0.5);
        effectText.setDepth(600);

        await new Promise<void>(resolve => {
            this.tweens.add({
                targets: effectText,
                y: y - 30,
                alpha: 0,
                duration: 800,
                ease: 'Cubic.easeOut',
                onComplete: () => {
                    effectText.destroy();
                    resolve();
                },
            });
        });
    }

    private async animateWeaponAttack(
        weapon: CombatModuleDisplay,
        target: EnemyDisplay,
        damage: number = 0,
        isEffective: boolean = false,
        isWeak: boolean = false
    ): Promise<void> {
        const weaponX = this.SPACE_X + weapon.container.x;
        const weaponY = weapon.container.y;
        const targetX = this.SPACE_X + target.container.x;
        const targetY = target.container.y;

        // Create enhanced projectile with glow
        const projectileContainer = this.add.container(weaponX, weaponY);
        projectileContainer.setDepth(500);

        // Outer glow layer
        const glow = this.add.graphics();
        glow.fillStyle(weapon.primaryColor, 0.3);
        glow.fillCircle(0, 0, 18);
        projectileContainer.add(glow);

        // Core projectile
        const projectile = this.add.graphics();
        projectile.fillStyle(weapon.primaryColor, 1);
        projectile.fillCircle(0, 0, 10);
        projectile.lineStyle(2, 0xffffff, 0.7);
        projectile.strokeCircle(0, 0, 12);
        // Inner highlight
        projectile.fillStyle(0xffffff, 0.5);
        projectile.fillCircle(-3, -3, 4);
        projectileContainer.add(projectile);

        // Recoil animation on weapon (stronger kick)
        this.tweens.add({
            targets: weapon.container,
            y: weapon.container.y + 15,
            duration: 60,
            yoyo: true,
            ease: 'Cubic.easeOut',
        });

        // Muzzle flash
        const muzzleFlash = this.add.graphics();
        muzzleFlash.fillStyle(weapon.primaryColor, 0.8);
        muzzleFlash.fillCircle(0, 0, 20);
        muzzleFlash.fillStyle(0xffffff, 0.6);
        muzzleFlash.fillCircle(0, 0, 10);
        muzzleFlash.setPosition(weaponX, weaponY);
        muzzleFlash.setDepth(499);

        this.tweens.add({
            targets: muzzleFlash,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 100,
            onComplete: () => muzzleFlash.destroy(),
        });

        // Trail particles during flight
        const trailParticles: Phaser.GameObjects.Graphics[] = [];
        const trailInterval = this.time.addEvent({
            delay: 25,
            callback: () => {
                const trail = this.add.graphics();
                trail.fillStyle(weapon.primaryColor, 0.5);
                trail.fillCircle(0, 0, 6);
                trail.setPosition(projectileContainer.x, projectileContainer.y);
                trail.setDepth(498);
                trailParticles.push(trail);

                this.tweens.add({
                    targets: trail,
                    alpha: 0,
                    scaleX: 0.3,
                    scaleY: 0.3,
                    duration: 150,
                    onComplete: () => {
                        trail.destroy();
                        const idx = trailParticles.indexOf(trail);
                        if (idx > -1) trailParticles.splice(idx, 1);
                    },
                });
            },
            loop: true,
        });

        // Animate projectile to target
        await new Promise<void>(resolve => {
            this.tweens.add({
                targets: projectileContainer,
                x: targetX,
                y: targetY,
                duration: 180,
                ease: 'Cubic.easeIn',
                onComplete: () => {
                    trailInterval.destroy();
                    projectileContainer.destroy();

                    // Hit explosion effect
                    this.createHitExplosion(targetX, targetY, weapon.primaryColor);

                    // Floating damage number
                    this.showDamageNumber(targetX, targetY, damage, weapon.primaryColor, isEffective, isWeak);

                    // Flash and recoil on hit
                    const origX = target.container.x;
                    this.tweens.add({
                        targets: target.container,
                        alpha: 0.6,
                        x: origX + 8,
                        duration: 40,
                        yoyo: true,
                        onComplete: () => resolve(),
                    });
                },
            });
        });
    }

    private createHitExplosion(x: number, y: number, color: number): void {
        // Central flash
        const flash = this.add.graphics();
        flash.fillStyle(0xffffff, 0.8);
        flash.fillCircle(0, 0, 15);
        flash.fillStyle(color, 0.6);
        flash.fillCircle(0, 0, 25);
        flash.setPosition(x, y);
        flash.setDepth(550);

        this.tweens.add({
            targets: flash,
            alpha: 0,
            scaleX: 2,
            scaleY: 2,
            duration: 150,
            ease: 'Cubic.easeOut',
            onComplete: () => flash.destroy(),
        });

        // Expanding ring
        const ring = this.add.graphics();
        ring.lineStyle(3, color, 0.8);
        ring.strokeCircle(0, 0, 20);
        ring.setPosition(x, y);
        ring.setDepth(549);

        this.tweens.add({
            targets: ring,
            alpha: 0,
            scaleX: 2.5,
            scaleY: 2.5,
            duration: 200,
            ease: 'Cubic.easeOut',
            onComplete: () => ring.destroy(),
        });

        // Spark particles (minimalist)
        const sparkCount = 6;
        for (let i = 0; i < sparkCount; i++) {
            const angle = (i / sparkCount) * Math.PI * 2;
            const spark = this.add.graphics();
            spark.fillStyle(color, 0.9);
            spark.fillCircle(0, 0, 4);
            spark.setPosition(x, y);
            spark.setDepth(548);

            const dist = 40 + Math.random() * 20;
            this.tweens.add({
                targets: spark,
                x: x + Math.cos(angle) * dist,
                y: y + Math.sin(angle) * dist,
                alpha: 0,
                scaleX: 0.2,
                scaleY: 0.2,
                duration: 180 + Math.random() * 60,
                ease: 'Cubic.easeOut',
                onComplete: () => spark.destroy(),
            });
        }
    }

    private showDamageNumber(
        x: number,
        y: number,
        damage: number,
        color: number,
        isEffective: boolean,
        isWeak: boolean
    ): void {
        // Determine styling based on effectiveness
        let fontSize = '28px';
        let textColor = '#ffffff';
        let suffix = '';

        if (isEffective) {
            fontSize = '34px';
            textColor = '#ffff00'; // Yellow for super effective
            suffix = '!';
        } else if (isWeak) {
            fontSize = '24px';
            textColor = '#888888'; // Gray for not effective
        }

        const damageText = this.add.text(x, y - 30, `${damage}${suffix}`, {
            fontFamily: 'monospace',
            fontSize,
            color: textColor,
            fontStyle: 'bold',
            stroke: '#000000',
            strokeThickness: 4,
        });
        damageText.setOrigin(0.5, 0.5);
        damageText.setDepth(600);

        // Pop-in effect
        damageText.setScale(0.5);

        this.tweens.add({
            targets: damageText,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 80,
            ease: 'Back.easeOut',
            onComplete: () => {
                this.tweens.add({
                    targets: damageText,
                    scaleX: 1,
                    scaleY: 1,
                    y: y - 80,
                    alpha: 0,
                    duration: 400,
                    ease: 'Cubic.easeOut',
                    onComplete: () => damageText.destroy(),
                });
            },
        });
    }

    private async animateEnemyDeath(enemy: EnemyDisplay): Promise<void> {
        // Award scrap based on enemy type
        const isBossEncounter = this.currentEncounter?.type === 'boss';
        let scrapReward: number;

        if (isBossEncounter) {
            // Boss gives budget value (from getBudgetsForThreatLevel)
            const budgets = getBudgetsForThreatLevel(this.threatLevel);
            scrapReward = budgets.boss;
        } else {
            // Regular enemies give threat level
            scrapReward = this.threatLevel;
        }

        // Apply threat level reward multiplier (0.5x extra per level above 1)
        const multipliers = getThreatMultipliers(this.threatLevel);
        scrapReward = Math.round(scrapReward * multipliers.rewardMult);

        this.scrapEarned += scrapReward;
        this.updateScrapDisplay();

        const enemyX = this.SPACE_X + enemy.container.x;
        const enemyY = enemy.container.y;
        const enemyAspect = enemy.enemy.aspects[0] || 'VOID';
        const enemyColor = ASPECT_COLORS[enemyAspect as ElementType] || 0xff4444;

        // Initial flash
        const whiteFlash = this.add.graphics();
        whiteFlash.fillStyle(0xffffff, 0.9);
        whiteFlash.fillCircle(0, 0, 60);
        whiteFlash.setPosition(enemyX, enemyY);
        whiteFlash.setDepth(700);

        this.tweens.add({
            targets: whiteFlash,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 100,
            onComplete: () => whiteFlash.destroy(),
        });

        // Create debris fragments from ship parts
        const fragmentCount = 8;
        const fragments: Phaser.GameObjects.Graphics[] = [];
        const scale = enemy.sprite.container.scaleX;

        for (let i = 0; i < fragmentCount; i++) {
            const fragment = this.add.graphics();
            const angle = (i / fragmentCount) * Math.PI * 2 + Math.random() * 0.5;

            // Random fragment shape (angular debris)
            const fragSize = 15 + Math.random() * 25;
            fragment.fillStyle(enemyColor, 0.8);
            fragment.beginPath();
            const points = 4 + Math.floor(Math.random() * 3);
            for (let p = 0; p < points; p++) {
                const pAngle = (p / points) * Math.PI * 2;
                const pDist = fragSize * (0.5 + Math.random() * 0.5);
                if (p === 0) {
                    fragment.moveTo(Math.cos(pAngle) * pDist, Math.sin(pAngle) * pDist);
                } else {
                    fragment.lineTo(Math.cos(pAngle) * pDist, Math.sin(pAngle) * pDist);
                }
            }
            fragment.closePath();
            fragment.fillPath();

            // Outline
            fragment.lineStyle(1, 0x666666, 0.5);
            fragment.strokePath();

            fragment.setPosition(enemyX, enemyY);
            fragment.setDepth(650);
            fragment.setScale(scale);
            fragments.push(fragment);

            // Animate fragment flying outward with rotation
            const distance = 80 + Math.random() * 100;
            const spin = (Math.random() - 0.5) * 8;

            this.tweens.add({
                targets: fragment,
                x: enemyX + Math.cos(angle) * distance,
                y: enemyY + Math.sin(angle) * distance + 50, // Slight downward drift
                rotation: spin,
                alpha: 0,
                scaleX: scale * 0.3,
                scaleY: scale * 0.3,
                duration: 400 + Math.random() * 200,
                ease: 'Cubic.easeOut',
                onComplete: () => fragment.destroy(),
            });
        }

        // Central explosion burst
        const burst = this.add.graphics();
        burst.fillStyle(enemyColor, 0.7);
        burst.fillCircle(0, 0, 40);
        burst.fillStyle(0xffffff, 0.5);
        burst.fillCircle(0, 0, 20);
        burst.setPosition(enemyX, enemyY);
        burst.setDepth(699);

        this.tweens.add({
            targets: burst,
            alpha: 0,
            scaleX: 2.5,
            scaleY: 2.5,
            duration: 250,
            ease: 'Cubic.easeOut',
            onComplete: () => burst.destroy(),
        });

        // Expanding shockwave ring
        const ring = this.add.graphics();
        ring.lineStyle(4, enemyColor, 0.8);
        ring.strokeCircle(0, 0, 30);
        ring.setPosition(enemyX, enemyY);
        ring.setDepth(698);

        this.tweens.add({
            targets: ring,
            alpha: 0,
            scaleX: 3,
            scaleY: 3,
            duration: 300,
            ease: 'Cubic.easeOut',
            onComplete: () => ring.destroy(),
        });

        // Second inner ring (delayed)
        this.time.delayedCall(80, () => {
            const ring2 = this.add.graphics();
            ring2.lineStyle(2, 0xffffff, 0.6);
            ring2.strokeCircle(0, 0, 20);
            ring2.setPosition(enemyX, enemyY);
            ring2.setDepth(697);

            this.tweens.add({
                targets: ring2,
                alpha: 0,
                scaleX: 2.5,
                scaleY: 2.5,
                duration: 250,
                ease: 'Cubic.easeOut',
                onComplete: () => ring2.destroy(),
            });
        });

        // Spark particles
        const sparkCount = 10;
        for (let i = 0; i < sparkCount; i++) {
            const spark = this.add.graphics();
            const sparkAngle = Math.random() * Math.PI * 2;
            spark.fillStyle(i < sparkCount / 2 ? enemyColor : 0xffaa00, 0.9);
            spark.fillCircle(0, 0, 3 + Math.random() * 3);
            spark.setPosition(enemyX, enemyY);
            spark.setDepth(696);

            const sparkDist = 60 + Math.random() * 80;
            this.tweens.add({
                targets: spark,
                x: enemyX + Math.cos(sparkAngle) * sparkDist,
                y: enemyY + Math.sin(sparkAngle) * sparkDist,
                alpha: 0,
                duration: 300 + Math.random() * 150,
                ease: 'Cubic.easeOut',
                onComplete: () => spark.destroy(),
            });
        }

        // Fade out the original enemy container quickly
        await new Promise<void>(resolve => {
            this.tweens.add({
                targets: enemy.container,
                alpha: 0,
                duration: 80,
                ease: 'Cubic.easeIn',
                onComplete: () => {
                    enemy.container.setVisible(false);
                    // Wait for explosion effects to mostly complete
                    this.time.delayedCall(250, () => resolve());
                },
            });
        });
    }

    private recenterEnemies(): void {
        const livingEnemies = this.enemyDisplays.filter(
            e => e.enemy.currentHP > 0 && e.container.visible
        );

        if (livingEnemies.length === 0) return;

        const enemyAreaTop = this.TOP_UI_HEIGHT;
        const centerX = this.SPACE_WIDTH / 2;
        const spacing = 20;

        // Calculate enemy size based on current count
        const baseScale = livingEnemies.length <= 2 ? 0.5 : livingEnemies.length <= 4 ? 0.4 : 0.3;
        const shipWidth = 250 * baseScale;

        const totalWidth = livingEnemies.length * shipWidth + (livingEnemies.length - 1) * spacing;
        let startX = centerX - totalWidth / 2 + shipWidth / 2;

        for (let i = 0; i < livingEnemies.length; i++) {
            const enemy = livingEnemies[i];
            const targetX = startX + i * (shipWidth + spacing);

            this.tweens.add({
                targets: enemy.container,
                x: targetX,
                duration: 300,
                ease: 'Cubic.easeOut',
            });
        }
    }

    private getSpeedMultiplier(): number {
        const { ANIMATION } = GAME_CONFIG;
        const mult = Math.pow(ANIMATION.CASCADE_SPEED_MULT, this.cascadeCount - 1);
        return Math.max(mult, ANIMATION.MIN_SPEED_MULT);
    }

    private findMatches(): { col: number; row: number }[][] {
        const { BOARD_COLS, BOARD_ROWS } = GAME_CONFIG;

        // Step 1: Find all horizontal line matches (3+ in a row)
        const horizontalMatches: { col: number; row: number; color: OrbColor }[][] = [];
        for (let row = 0; row < BOARD_ROWS; row++) {
            let col = 0;
            while (col < BOARD_COLS - 2) {
                const color = this.board[row][col];
                if (!color || this.isCellLocked(col, row)) {
                    col++;
                    continue;
                }
                // Find run of same color
                const match: { col: number; row: number; color: OrbColor }[] = [{ col, row, color }];
                let c = col + 1;
                while (c < BOARD_COLS && this.board[row][c] === color && !this.isCellLocked(c, row)) {
                    match.push({ col: c, row, color });
                    c++;
                }
                if (match.length >= 3) {
                    horizontalMatches.push(match);
                }
                col = c;
            }
        }

        // Step 2: Find all vertical line matches (3+ in a column)
        const verticalMatches: { col: number; row: number; color: OrbColor }[][] = [];
        for (let col = 0; col < BOARD_COLS; col++) {
            let row = 0;
            while (row < BOARD_ROWS - 2) {
                const color = this.board[row][col];
                if (!color || this.isCellLocked(col, row)) {
                    row++;
                    continue;
                }
                // Find run of same color
                const match: { col: number; row: number; color: OrbColor }[] = [{ col, row, color }];
                let r = row + 1;
                while (r < BOARD_ROWS && this.board[r][col] === color && !this.isCellLocked(col, r)) {
                    match.push({ col, row: r, color });
                    r++;
                }
                if (match.length >= 3) {
                    verticalMatches.push(match);
                }
                row = r;
            }
        }

        // Step 3: Merge intersecting matches of the same color using union-find
        const allLineMatches = [...horizontalMatches, ...verticalMatches];
        if (allLineMatches.length === 0) return [];

        // Build a map of cell -> which line matches contain it
        const cellToMatches: Map<string, number[]> = new Map();
        for (let i = 0; i < allLineMatches.length; i++) {
            for (const cell of allLineMatches[i]) {
                const key = `${cell.col},${cell.row}`;
                if (!cellToMatches.has(key)) {
                    cellToMatches.set(key, []);
                }
                cellToMatches.get(key)!.push(i);
            }
        }

        // Union-find to merge matches that share cells of the same color
        const parent: number[] = allLineMatches.map((_, i) => i);
        const find = (x: number): number => {
            if (parent[x] !== x) parent[x] = find(parent[x]);
            return parent[x];
        };
        const union = (a: number, b: number): void => {
            const pa = find(a), pb = find(b);
            if (pa !== pb) parent[pa] = pb;
        };

        // Merge matches that share cells and have same color
        for (const matchIndices of cellToMatches.values()) {
            if (matchIndices.length > 1) {
                const firstColor = allLineMatches[matchIndices[0]][0].color;
                for (let i = 1; i < matchIndices.length; i++) {
                    const otherColor = allLineMatches[matchIndices[i]][0].color;
                    if (firstColor === otherColor) {
                        union(matchIndices[0], matchIndices[i]);
                    }
                }
            }
        }

        // Group matches by their root parent
        const groups: Map<number, Set<string>> = new Map();
        for (let i = 0; i < allLineMatches.length; i++) {
            const root = find(i);
            if (!groups.has(root)) {
                groups.set(root, new Set());
            }
            const group = groups.get(root)!;
            for (const cell of allLineMatches[i]) {
                group.add(`${cell.col},${cell.row}`);
            }
        }

        // Convert groups to final match arrays
        const matches: { col: number; row: number }[][] = [];
        for (const cellKeys of groups.values()) {
            const match: { col: number; row: number }[] = [];
            for (const key of cellKeys) {
                const [col, row] = key.split(',').map(Number);
                match.push({ col, row });
            }
            matches.push(match);
        }

        return matches;
    }

    private animateMatchFade(matches: { col: number; row: number }[][], speedMult: number): Promise<void> {
        const { ANIMATION, ORB_COLORS } = GAME_CONFIG;
        const duration = ANIMATION.MATCH_FADE_DURATION * speedMult;

        return new Promise(resolve => {
            let completed = 0;
            let total = 0;
            const animated = new Set<string>();

            for (const match of matches) {
                for (const pos of match) {
                    const key = `${pos.col},${pos.row}`;
                    if (animated.has(key)) continue;
                    animated.add(key);

                    const orb = this.orbs[pos.row][pos.col];
                    if (orb) {
                        total++;

                        // Get orb screen position for particle effect
                        const orbX = orb.container.x + this.boardContainer.x;
                        const orbY = orb.container.y + this.boardContainer.y;
                        const orbColor = ORB_COLORS[orb.color];

                        // Create burst particles
                        this.createMatchBurst(orbX, orbY, orbColor, speedMult);

                        // Scale up with flash then fade
                        this.tweens.add({
                            targets: orb.container,
                            scaleX: 1.3,
                            scaleY: 1.3,
                            alpha: 0,
                            duration: duration,
                            ease: 'Back.easeIn',
                            onComplete: () => {
                                completed++;
                                if (completed >= total) resolve();
                            },
                        });

                        // Quick bright flash at start
                        if (orb.glowGraphics) {
                            this.tweens.add({
                                targets: orb.glowGraphics,
                                alpha: 1,
                                duration: duration * 0.3,
                                yoyo: true,
                            });
                        }
                    }
                }
            }

            if (total === 0) resolve();
        });
    }

    private createMatchBurst(x: number, y: number, color: number, speedMult: number): void {
        // Central flash
        const flash = this.add.graphics();
        flash.fillStyle(0xffffff, 0.6);
        flash.fillCircle(0, 0, 20);
        flash.fillStyle(color, 0.4);
        flash.fillCircle(0, 0, 30);
        flash.setPosition(x, y);
        flash.setDepth(200);

        this.tweens.add({
            targets: flash,
            alpha: 0,
            scaleX: 1.5,
            scaleY: 1.5,
            duration: 100 * speedMult,
            ease: 'Cubic.easeOut',
            onComplete: () => flash.destroy(),
        });

        // Small burst particles (4-5 particles, subtle)
        const particleCount = 4 + Math.floor(Math.random() * 2);
        for (let i = 0; i < particleCount; i++) {
            const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.5;
            const particle = this.add.graphics();
            particle.fillStyle(color, 0.8);
            particle.fillCircle(0, 0, 4 + Math.random() * 3);
            particle.setPosition(x, y);
            particle.setDepth(199);

            const dist = 25 + Math.random() * 20;
            this.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * dist,
                y: y + Math.sin(angle) * dist,
                alpha: 0,
                scaleX: 0.3,
                scaleY: 0.3,
                duration: (120 + Math.random() * 60) * speedMult,
                ease: 'Cubic.easeOut',
                onComplete: () => particle.destroy(),
            });
        }
    }

    private animateGravity(speedMult: number): Promise<void> {
        const { BOARD_COLS, BOARD_ROWS, ANIMATION } = GAME_CONFIG;

        return new Promise(resolve => {
            const movements: Array<{
                orb: OrbData;
                fromRow: number;
                toRow: number;
                col: number;
            }> = [];

            for (let col = 0; col < BOARD_COLS; col++) {
                let writeRow = BOARD_ROWS - 1;

                for (let row = BOARD_ROWS - 1; row >= 0; row--) {
                    if (this.board[row][col] !== null) {
                        if (row !== writeRow) {
                            const orb = this.orbs[row][col];
                            if (orb) {
                                movements.push({ orb, fromRow: row, toRow: writeRow, col });
                            }

                            this.board[writeRow][col] = this.board[row][col];
                            this.board[row][col] = null;
                            this.orbs[writeRow][col] = this.orbs[row][col];
                            this.orbs[row][col] = null;
                        }
                        writeRow--;
                    }
                }
            }

            if (movements.length === 0) {
                resolve();
                return;
            }

            let completed = 0;

            for (const movement of movements) {
                const targetPos = this.getOrbPosition(movement.col, movement.toRow);
                const distance = movement.toRow - movement.fromRow;
                const duration = ANIMATION.GRAVITY_DURATION * distance * speedMult;

                this.tweens.add({
                    targets: movement.orb.container,
                    y: targetPos.y,
                    duration: duration,
                    ease: 'Bounce.easeOut',
                    onComplete: () => {
                        completed++;
                        if (completed >= movements.length) resolve();
                    },
                });
            }
        });
    }

    private animateSpawn(speedMult: number): Promise<void> {
        const { BOARD_COLS, BOARD_ROWS, ORB_SIZE, ORB_SPACING, ANIMATION } = GAME_CONFIG;
        const cellSize = ORB_SIZE + ORB_SPACING;

        return new Promise(resolve => {
            const spawns: Array<{ col: number; row: number }> = [];

            for (let col = 0; col < BOARD_COLS; col++) {
                for (let row = 0; row < BOARD_ROWS; row++) {
                    if (this.board[row][col] === null) {
                        const color = this.getRandomOrbColor();
                        this.board[row][col] = color;

                        const orbData = this.createOrb(col, row, color);
                        orbData.container.y = -cellSize;
                        orbData.container.setAlpha(0);

                        spawns.push({ col, row });
                    }
                }
            }

            if (spawns.length === 0) {
                resolve();
                return;
            }

            let completed = 0;

            for (const spawn of spawns) {
                const orb = this.orbs[spawn.row][spawn.col];
                if (orb) {
                    const targetPos = this.getOrbPosition(spawn.col, spawn.row);
                    const distance = spawn.row + 1;
                    const duration = ANIMATION.GRAVITY_DURATION * distance * speedMult;

                    this.tweens.add({
                        targets: orb.container,
                        alpha: 1,
                        duration: 50,
                    });

                    this.tweens.add({
                        targets: orb.container,
                        y: targetPos.y,
                        duration: duration,
                        ease: 'Bounce.easeOut',
                        onComplete: () => {
                            completed++;
                            if (completed >= spawns.length) resolve();
                        },
                    });
                }
            }
        });
    }
}
